import React, { useEffect, useState, useImperativeHandle, useCallback, useRef, useMemo } from 'react';
import { Table, Button, Dropdown, Badge, Placeholder, OverlayTrigger, Tooltip, Modal } from 'react-bootstrap';
import { createLogger } from '../utils/debugLogger';
import CategorySelect from './CategorySelect';
import ArmorEdit from './ArmorEdit';
import StarRating from './StarRating';
import TSizeEditor from './TSizeEditor';
import { getTickerShields, getTickerNotes, updateAccountTickerTheme, updateAccountTickerBase, updateAccountTickerEntryRating } from '../services/api';
import NextPlanEditor from './NextPlanEditor';
import ContextMenu from './ContextMenu';
import ManualPriceModal from './ManualPriceModal';
import ColumnVisibilityDropdown from './ColumnVisibilityDropdown';
import { formatColumnLabel } from '../utils/formatColumnLabel';
import './TickerTable.css'; // Import CSS for additional styling
import { Link } from 'react-router-dom';
import { FaStar, FaRegStar, FaHammer } from 'react-icons/fa';
import { BsShieldFill } from 'react-icons/bs';
import NotesModal from './NotesModal';
import { setThemeOverride } from '../utils/themeOverrides';
import { setBaseOverride } from '../utils/baseOverrides';
import { formatPositionSizeFraction } from '../utils/positionSizeFormat';

const THEME_ICON_MAIN_SIZE = '1.375rem';
const THEME_ICON_SUB_SIZE = '1.21rem';

const renderThemeIcon = (enabled, size = THEME_ICON_MAIN_SIZE) => (
  enabled
    ? <FaStar style={{ color: '#f5c518', fontSize: size }} />
    : <FaRegStar style={{ color: '#b0b0b0', fontSize: size }} />
);

const getAggregatedThemeState = (ticker) => {
  if (ticker && typeof ticker.theme_enabled === 'boolean') {
    return ticker.theme_enabled;
  }

  if (ticker && Array.isArray(ticker.accounts) && ticker.accounts.length > 0) {
    return ticker.accounts.some(account => account.theme_enabled);
  }

  return false;
};

const getAggregatedBaseState = (ticker) => {
  if (ticker && typeof ticker.base_enabled === 'boolean') {
    return ticker.base_enabled;
  }

  if (ticker && Array.isArray(ticker.accounts) && ticker.accounts.length > 0) {
    return ticker.accounts.some(account => account.base_enabled);
  }

  return false;
};

// Create debug logger for this module
const logger = createLogger('tickerTable', 'Ticker table data rendering and interactions', false, 'INFO');

// Custom styles for enhanced table styling
const customStyles = `
    /* Enhanced table styling for maximum contrast and prominence */
    .tickers-table {
        background-color: var(--card-bg) !important;
        border: 2px solid var(--border-color) !important;
        border-radius: 8px !important;
        box-shadow: 0 4px 16px var(--shadow-color) !important;
        overflow: visible !important;
        transition: all 0.3s ease-in-out;
    }
    
    .tickers-table:hover {
        box-shadow: 0 8px 24px var(--shadow-color) !important;
    }

    /* Column dropdown toggle enhancements */
    .column-toggle-switch .form-check-input {
        width: 2.25em !important;
        height: 1.25em !important;
        margin-right: 0.4rem;
        cursor: pointer;
        transition: all 0.2s ease-in-out;
    }

    .column-toggle-switch .form-check-input:checked {
        background-color: #0d6efd !important;
        border-color: #0d6efd !important;
        box-shadow: 0 0 6px rgba(13, 110, 253, 0.65) !important;
    }

    .column-toggle-switch .form-check-input:focus {
        border-color: #0d6efd !important;
        box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25) !important;
    }

    /* Light theme table enhancements */
    [data-theme="light"] .tickers-table {
        background-color: #ffffff !important;
        border: 2px solid #d1d5db !important;
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.05), 0 4px 20px rgba(0, 0, 0, 0.15) !important;
    }
    
    [data-theme="light"] .tickers-table:hover {
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1), 0 8px 32px rgba(0, 0, 0, 0.25) !important;
        border-color: #9ca3af !important;
    }
    
    /* Dark theme table enhancements */
    [data-theme="dark"] .tickers-table {
        background-color: #1f2937 !important;
        border: 2px solid #4b5563 !important;
        box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.05), 0 4px 20px rgba(0, 0, 0, 0.5) !important;
    }
    
    [data-theme="dark"] .tickers-table:hover {
        box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.1), 0 8px 32px rgba(0, 0, 0, 0.6) !important;
        border-color: #6b7280 !important;
        background-color: #374151 !important;
    }
    
    /* Enhanced table header styling - override TickerTable.css */
    .ticker-table.tickers-table thead th,
    .tickers-table thead th,
    .tickers-table-header {
        background-color: var(--bg-secondary) !important;
        border-bottom: 2px solid var(--border-color) !important;
        font-weight: 600 !important;
        color: var(--text-primary) !important;
        padding: 6px 6px !important;
        transition: background-color 0.2s ease-in-out;
    }
    
    .ticker-table.tickers-table thead th:hover,
    .tickers-table thead th:hover,
    .tickers-table-header:hover {
        background-color: var(--hover-bg) !important;
        cursor: pointer;
    }
    
    [data-theme="light"] .ticker-table.tickers-table thead th,
    [data-theme="light"] .tickers-table thead th,
    [data-theme="light"] .tickers-table-header {
        background-color: #f8f9fa !important;
        border-bottom: 2px solid #d1d5db !important;
        color: #374151 !important;
    }
    
    [data-theme="light"] .ticker-table.tickers-table thead th:hover,
    [data-theme="light"] .tickers-table thead th:hover,
    [data-theme="light"] .tickers-table-header:hover {
        background-color: #e9ecef !important;
    }
    
    [data-theme="dark"] .ticker-table.tickers-table thead th,
    [data-theme="dark"] .tickers-table thead th,
    [data-theme="dark"] .tickers-table-header {
        background-color: #374151 !important;
        border-bottom: 2px solid #4b5563 !important;
        color: #f9fafb !important;
    }
    
    [data-theme="dark"] .ticker-table.tickers-table thead th:hover,
    [data-theme="dark"] .tickers-table thead th:hover,
    [data-theme="dark"] .tickers-table-header:hover {
        background-color: #4b5563 !important;
    }
    
    /* Enhanced table row styling */
    .tickers-table tbody tr {
        border-bottom: 1px solid var(--border-color) !important;
        transition: background-color 0.2s ease-in-out;
    }
    
    .tickers-table tbody tr:hover {
        background-color: var(--hover-bg) !important;
        transform: translateY(-1px) !important;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
    }

    /* Ensure dropdown menus in table are above rows */
    .tickers-table .dropdown-menu {
        z-index: 100000 !important;
    }
    
    [data-theme="light"] .tickers-table tbody tr:hover {
        background-color: #f1f5f9 !important;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
    }
    
    [data-theme="dark"] .tickers-table tbody tr:hover {
        background-color: #4b5563 !important;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3) !important;
    }
    
    /* Enhanced table cell styling - more compact */
    .tickers-table tbody td {
        padding: 4px 6px !important;
        border-bottom: 1px solid var(--border-color) !important;
        vertical-align: middle !important;
    }
    
    [data-theme="light"] .tickers-table tbody td {
        border-bottom: 1px solid #e5e7eb !important;
    }
    
    [data-theme="dark"] .tickers-table tbody td {
        border-bottom: 1px solid #374151 !important;
    }
    
    /* Tooltip theme support - override Bootstrap defaults */
    [data-theme="light"] .tooltip .tooltip-inner {
        background-color: #ffffff !important;
        color: #212529 !important;
        border: 1px solid #d1d5db !important;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
    }
    
    [data-theme="dark"] .tooltip .tooltip-inner {
        background-color: #1f2937 !important;
        color: #f9fafb !important;
        border: 1px solid #4b5563 !important;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3) !important;
    }
    
    /* Override Bootstrap tooltip arrow colors */
    [data-theme="light"] .tooltip .tooltip-arrow::before {
        border-top-color: #ffffff !important;
    }
    
    [data-theme="dark"] .tooltip .tooltip-arrow::before {
        border-top-color: #1f2937 !important;
    }
`;

const STRATEGY_CONFIG_MAP = {
  progress: { key: 'progress', label: 'Progress', variant: 'success', stopLossPercent: null },
  base: { key: 'base', label: 'Base', variant: 'primary', stopLossPercent: -7 },
  long: { key: 'long', label: 'Long', variant: 'info', stopLossPercent: -9 },
  lllong: { key: 'lllong', label: 'LLLong', variant: 'info', stopLossPercent: -15 },
  llong: { key: 'llong', label: 'LLong', variant: 'info', stopLossPercent: -12 },
  volatile: { key: 'volatile', label: 'Volatile', variant: 'warning', textColor: '#1f2937', stopLossPercent: -5 },
  strength: { key: 'strength', label: 'Strength', variant: 'success', stopLossPercent: -3 },
  tight: { key: 'tight', label: 'Tight', variant: 'primary', stopLossPercent: -2 },
  'T2-5-9': { key: 'T2-5-9', label: 'T2-5-9', variant: 'success', stopLossPercent: null },
  'T3-7-12': { key: 'T3-7-12', label: 'T3-7-12', variant: 'success', stopLossPercent: null },
  'T5-10-15': { key: 'T5-10-15', label: 'T5-10-15', variant: 'success', stopLossPercent: null }
};

const STRATEGY_PRIORITY = ['progress', 'tight', 'volatile', 'strength', 'lllong', 'llong', 'long', 'base'];

const ENTRY_STRATEGY_STYLES = {
  2:  { variant: 'primary' },
  3:  { variant: 'primary' },
  4:  { variant: 'primary' },
  5:  { variant: 'primary' },
  6:  { variant: 'warning', textColor: '#212529' },
  7:  { variant: 'warning', textColor: '#212529' },
  8:  { variant: 'warning', textColor: '#212529' },
  9:  { variant: 'danger' },
  10: { variant: 'danger' },
  11: { variant: 'danger' },
  12: { variant: 'danger' },
  15: { variant: 'danger' }
};

// Named single-stop strategies map to their canonical entry key so they render
// as T{pct} badges instead of verbose labels like "Base" or "Volatile".
const NAMED_STRATEGY_ENTRY_KEY = {
  tight:    'entry-T2',
  strength: 'entry-T3',
  volatile: 'entry-T5',
  base:     'entry-T7',
  long:     'entry-T9',
  llong:    'entry-T12',
  lllong:   'entry-T15',
};

const ENTRY_RATING_STYLES = {
  EARLY: { label: 'EARLY', variant: 'warning', textClass: 'text-dark' },
  EXACT: { label: 'EXACT', variant: 'success', textClass: 'text-white' },
  LATE: { label: 'LATE', variant: 'warning', textClass: 'text-dark' },
  HOT: { label: 'HOT', variant: 'danger', textClass: 'text-white' }
};

const normalizeEntryRatingValue = (value) => {
  if (value === null || value === undefined) {
    return '';
  }
  const normalized = String(value).trim().toUpperCase();
  return normalized === 'GOOD' ? 'EXACT' : normalized;
};

const deriveStrategyKeyFromText = (value) => {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim().toLowerCase();
  if (!normalized) {
    return null;
  }

  if (normalized.includes('progress')) {
    return 'progress';
  }

  if (normalized.includes('tight')) {
    return 'tight';
  }

  if (normalized.includes('volatile')) {
    return 'volatile';
  }

  if (normalized.includes('strength')) {
    return 'strength';
  }

  // Check for lllong/lllong before llong/long to avoid false matches
  if (normalized.includes('lllong') || normalized.includes('long long long')) {
    return 'lllong';
  }

  if (normalized.includes('llong') || normalized.includes('long long')) {
    return 'llong';
  }

  if (normalized.includes('long')) {
    return 'long';
  }

  if (normalized.includes('base')) {
    return 'base';
  }

  if (normalized.includes('base play') || normalized.startsWith('base play')) {
    return 'base';
  }

  // Generic: any "<name> play -N%" trailing stop preset
  const playMatch = normalized.match(/\bplay\b.*-\s*(\d+)%/);
  if (playMatch) {
    const pct = parseInt(playMatch[1], 10);
    return `play-T${pct}`;
  }

  return null;
};

const deriveStrategyKeyFromTickerFields = (ticker) => {
  if (!ticker) {
    return null;
  }

  const possibleValues = [
    ticker.strategy_label,
    ticker.strategy,
    ticker.strategy_preset,
    ticker.current_strategy,
    ticker.armor
  ];

  for (const value of possibleValues) {
    const key = deriveStrategyKeyFromText(value);
    if (key) {
      return key;
    }
  }

  return null;
};

const deriveStrategyKeyFromShields = (shields = []) => {
  if (!Array.isArray(shields) || shields.length === 0) {
    return null;
  }

  const matchedKeys = new Set();
  let hasCustomShield = false;
  const profitPercentages = [];

  // First check for profit-taking strategies and detect custom shields
  shields.forEach((shield) => {
    const shieldName = shield?.shield_name || shield?.name || '';
    const key = deriveStrategyKeyFromText(shieldName);
    if (key) {
      matchedKeys.add(key);
    } else if (shieldName.trim()) {
      // Check for profit percentage patterns like "Profit 3%", "purchase +5%", etc.
      const normalized = shieldName.toLowerCase().trim();
      const profitMatch = normalized.match(/(?:profit|purchase)\s*\+?\s*(\d+)%/);
      if (profitMatch) {
        const pct = parseInt(profitMatch[1], 10);
        if (pct >= 0 && pct <= 100) {
          profitPercentages.push(pct);
        }
      } else {
        // Check if this is a custom shield (not matching preset patterns)
        // Standard patterns include: "stop -3%", "trailing -5%", "t3%-trim", "t7%-exit", "f5%-exit",
        // and any "<name> play -N%" trailing stop preset
        const isStandardPattern = /^(stop|trailing)\s*-\s*\d+%$/.test(normalized) ||
                                  /^[tf]\d+%-(?:trim|exit)$/.test(normalized) ||
                                  /^t\d+%$/.test(normalized) ||
                                  /\bplay\b.*-\s*\d+%/.test(normalized);
        if (!isStandardPattern) {
          hasCustomShield = true;
        }
      }
    }
  });

  // If we found profit percentage shields, return profit-X strategy
  if (profitPercentages.length > 0) {
    // Use the highest profit percentage found
    const maxProfit = Math.max(...profitPercentages);
    return `profit-${maxProfit}`;
  }

  // Check for all-in-one trim strategy patterns (T2-5-9, T3-7-12) BEFORE checking for progress
  // This ensures T patterns take priority over the "custom shield = progress" logic
  const trimPercentages = [];
  shields.forEach((shield) => {
    const shieldName = (shield?.shield_name || shield?.name || '').toLowerCase().trim();
    // Match patterns like "t2%-trim", "t5%-trim", "t9%-exit", "t12%-exit"
    const trimMatch = shieldName.match(/t(\d+)%-(?:trim|exit)/);
    if (trimMatch) {
      const pct = parseInt(trimMatch[1], 10);
      trimPercentages.push(pct);
    }
  });

  // Check if we have exactly 3 shields matching the all-in-one patterns
  if (trimPercentages.length === 3) {
    const sorted = [...trimPercentages].sort((a, b) => a - b);
    // T2-5-9 pattern
    if (sorted[0] === 2 && sorted[1] === 5 && sorted[2] === 9) {
      return 'T2-5-9';
    }
    // T3-7-12 pattern
    if (sorted[0] === 3 && sorted[1] === 7 && sorted[2] === 12) {
      return 'T3-7-12';
    }
    // T5-10-15 pattern
    if (sorted[0] === 5 && sorted[1] === 10 && sorted[2] === 15) {
      return 'T5-10-15';
    }
  }

  // Check for T-F 2-shield patterns (T2-F5, T3-F7, T5-F10)
  // One trailing trim shield + one fixed-price exit shield
  // Matches both preset names (t3%-trim / f7%-exit) and stop names (Trailing -3% / Stop -7%)
  let tfTrailingPct = null;
  let tfFixedPct = null;
  shields.forEach((shield) => {
    const shieldName = (shield?.shield_name || shield?.name || '').toLowerCase().trim();
    const trimMatch = shieldName.match(/^t(\d+)%-trim$/);
    if (trimMatch) {
      tfTrailingPct = parseInt(trimMatch[1], 10);
    }
    const exitMatch = shieldName.match(/^f(\d+)%-exit$/);
    if (exitMatch) {
      tfFixedPct = parseInt(exitMatch[1], 10);
    }
    const trailingStopMatch = shieldName.match(/^trailing\s*-\s*(\d+)%$/);
    if (trailingStopMatch && tfTrailingPct === null) {
      tfTrailingPct = parseInt(trailingStopMatch[1], 10);
    }
    const fixedStopMatch = shieldName.match(/^stop\s*-\s*(\d+)%$/);
    if (fixedStopMatch && tfFixedPct === null) {
      tfFixedPct = parseInt(fixedStopMatch[1], 10);
    }
  });
  if (tfTrailingPct !== null && tfFixedPct !== null) {
    return `T${tfTrailingPct}-F${tfFixedPct}`;
  }

  // If we have progress explicitly set, return it first
  if (matchedKeys.has('progress')) {
    return 'progress';
  }

  // If we detected custom shields, mark as progress
  if (hasCustomShield && shields.length > 0) {
    return 'progress';
  }

  // Check other profit-taking strategies
  for (const priorityKey of STRATEGY_PRIORITY) {
    if (priorityKey !== 'progress' && matchedKeys.has(priorityKey)) {
      return priorityKey;
    }
  }

  // If no profit-taking strategy found, check for entry strategies
  // Look for patterns like "Stop -3%", "Trailing -5%", "3%", etc.
  const entryStops = [];

  // Collect any generic play-T* keys already identified by deriveStrategyKeyFromText
  matchedKeys.forEach((k) => {
    const playTMatch = typeof k === 'string' && k.match(/^play-T(\d+)$/);
    if (playTMatch) {
      const pct = parseInt(playTMatch[1], 10);
      entryStops.push({ token: `T${pct}`, pct });
    }
  });

  shields.forEach((shield) => {
    const shieldName = (shield?.shield_name || shield?.name || '').toLowerCase().trim();

    // Skip if it contains profit-taking keywords
    if (shieldName.includes('base') || shieldName.includes('volatile') ||
        shieldName.includes('strength') || shieldName.includes('tight')) {
      return;
    }

    // Skip if already handled as a generic play preset
    if (/\bplay\b.*-\s*\d+%/.test(shieldName)) {
      return;
    }

    // "t7%-trim" / "t12%-exit" / "t15%-exit" style shields (all-in-one) → always trailing
    const trimMatch = shieldName.match(/^t(\d+)%-(?:trim|exit)/);
    if (trimMatch) {
      const pct = parseInt(trimMatch[1], 10);
      entryStops.push({ token: `T${pct}`, pct });
      return;
    }
    // "f5%-exit" / "f7%-exit" style shields — fixed-price floor remaining after trailing stop triggered
    const fixedExitMatch = shieldName.match(/^f(\d+)%-exit$/);
    if (fixedExitMatch) {
      const pct = parseInt(fixedExitMatch[1], 10);
      entryStops.push({ token: `F${pct}`, pct });
      return;
    }
    // "t15%" bare trailing percentage (no trim/exit suffix)
    const bareTrailingMatch = shieldName.match(/^t(\d+)%$/);
    if (bareTrailingMatch) {
      const pct = parseInt(bareTrailingMatch[1], 10);
      entryStops.push({ token: `T${pct}`, pct });
      return;
    }
    // "Stop -7%" / "Trailing -7%" style shields
    const percentMatch = shieldName.match(/^(trailing|stop)?\s*-?\s*(\d+)%/);
    if (percentMatch) {
      const typePrefix = percentMatch[1] === 'trailing' ? 'T' : 'F';
      const pct = parseInt(percentMatch[2], 10);
      entryStops.push({ token: `${typePrefix}${pct}`, pct });
    }
  });

  if (entryStops.length > 0) {
    // Deduplicate by token, sort ascending by percentage
    const seen = new Set();
    const unique = entryStops
      .filter(({ token }) => seen.has(token) ? false : seen.add(token))
      .sort((a, b) => a.pct - b.pct);

    return `entry-${unique.map(s => s.token).join('-')}`;
  }

  return null;
};

const deriveStrategyKeyFromTickerShields = (ticker) => {
  if (!ticker) {
    return null;
  }

  const shields = ticker?.shields;

  if (Array.isArray(shields) && shields.length > 0) {
    return deriveStrategyKeyFromShields(shields);
  }

  if (Array.isArray(shields?.data) && shields.data.length > 0) {
    return deriveStrategyKeyFromShields(shields.data);
  }

  return null;
};

// Determine which stop level has the largest allocation for all-in strategies
// This function works directly with shields array, not ticker object
const getEmphasizedStopLevelFromShields = (shields, strategyKey) => {
  if (!strategyKey || !['T2-5-9', 'T3-7-12', 'T5-10-15'].includes(strategyKey)) {
    return null;
  }

  if (!Array.isArray(shields) || shields.length !== 3) {
    return null;
  }

  // Map shield names to their stop levels and share counts
  const stopLevels = [];
  shields.forEach((shield) => {
    const shieldName = (shield?.shield_name || shield?.name || '').toLowerCase().trim();
    const trimMatch = shieldName.match(/t(\d+)%-(?:trim|exit)/);
    if (trimMatch) {
      const pct = parseInt(trimMatch[1], 10);
      const shares = parseFloat(shield?.shield_shares || shield?.shield_percentage || 0);
      stopLevels.push({ level: pct, shares });
    }
  });

  if (stopLevels.length !== 3) {
    return null;
  }

  // Find the stop level with the most shares
  stopLevels.sort((a, b) => b.shares - a.shares);
  return stopLevels[0].level;
};

// Calculate the effective percentage for all-in strategies based on distribution
const getEffectiveStrategyPercentage = (shields, strategyKey) => {
  if (!strategyKey || !['T2-5-9', 'T3-7-12', 'T5-10-15'].includes(strategyKey)) {
    return null;
  }

  if (!Array.isArray(shields) || shields.length !== 3) {
    return null;
  }

  // Map shield names to their stop levels and share counts
  const stopLevels = [];
  let totalShares = 0;

  shields.forEach((shield) => {
    const shieldName = (shield?.shield_name || shield?.name || '').toLowerCase().trim();
    const trimMatch = shieldName.match(/t(\d+)%-(?:trim|exit)/);
    if (trimMatch) {
      const pct = parseInt(trimMatch[1], 10);
      const shares = parseFloat(shield?.shield_shares || shield?.shield_percentage || 0);
      stopLevels.push({ level: pct, shares });
      totalShares += shares;
    }
  });

  if (stopLevels.length !== 3 || totalShares === 0) {
    return null;
  }

  // Calculate weighted average: (level1 * shares1/total + level2 * shares2/total + level3 * shares3/total)
  const effectivePercentage = stopLevels.reduce((sum, { level, shares }) => {
    return sum + (level * (shares / totalShares));
  }, 0);

  return effectivePercentage;
};

const getEntryStrategyStyle = (percentages) => {
  if (!percentages || percentages.length === 0) {
    return { variant: 'primary' };
  }

  const sorted = [...percentages].sort((a, b) => a - b);
  const highest = sorted[sorted.length - 1];
  return ENTRY_STRATEGY_STYLES[highest] || { variant: 'primary' };
};

const getStrategyConfigForKey = (key) => {
  if (!key) {
    return null;
  }

  // Named single-stop strategies (base, volatile, strength, etc.) render as T{pct} badges
  if (NAMED_STRATEGY_ENTRY_KEY[key]) {
    return getStrategyConfigForKey(NAMED_STRATEGY_ENTRY_KEY[key]);
  }

  // Check if it's a static strategy key
  if (STRATEGY_CONFIG_MAP[key]) {
    return STRATEGY_CONFIG_MAP[key];
  }

  // Check if it's a dynamic profit strategy key
  if (typeof key === 'string' && key.startsWith('profit-')) {
    const percentPart = key.substring(7); // Remove 'profit-' prefix
    const percentage = parseInt(percentPart, 10);

    if (!isNaN(percentage)) {
      return {
        key: key,
        label: `PROFIT+${percentage}%`,
        variant: 'success'
      };
    }
  }

  // Check if it's a dynamic T-F hybrid strategy key (e.g. T2-F5, T3-F10)
  if (typeof key === 'string' && /^T\d+-F\d+$/.test(key)) {
    return { key, label: key, variant: 'success', stopLossPercent: null };
  }

  // Check if it's a dynamic entry strategy key
  if (typeof key === 'string' && key.startsWith('entry-')) {
    const tokenPart = key.substring(6); // Remove 'entry-' prefix
    // Tokens are either typed (T7, F12) or legacy plain numbers
    const tokens = tokenPart.split('-').filter(Boolean);

    // Parse each token: typed format "T7"/"F12" or legacy plain number
    const parsed = tokens.map(t => {
      const typedMatch = t.match(/^([TF])(\d+)$/);
      if (typedMatch) return { prefix: typedMatch[1], pct: parseInt(typedMatch[2], 10) };
      const pct = parseInt(t, 10);
      return isNaN(pct) ? null : { prefix: 'F', pct };
    }).filter(Boolean);

    if (parsed.length === 0) return null;

    const numericPercents = parsed.map(p => p.pct);
    const entryStyle = getEntryStrategyStyle(numericPercents);

    if (parsed.length === 1) {
      if (parsed[0].pct === 0) {
        return { key, label: 'BreakEven', variant: 'success', textColor: '#ffffff' };
      }
      return {
        key,
        label: `${parsed[0].prefix}${parsed[0].pct}`,
        variant: entryStyle.variant,
        textColor: entryStyle.textColor
      };
    }

    const allSameType = parsed.every(p => p.prefix === parsed[0].prefix);
    const label = allSameType
      ? `${parsed[0].prefix}${parsed.map(p => p.pct).join('-')}`
      : parsed.map(p => `${p.prefix}${p.pct}`).join('-');
    return {
      key,
      label,
      variant: entryStyle.variant,
      textColor: entryStyle.textColor
    };
  }

  return null;
};

const TickerTable = React.memo(React.forwardRef(({
  tickers,
  accountStandardSize,
  accountTotalValue,
  onTickerUpdate,
  accountId,
  hideColumnsDropdown = false,
  onToggleColumnVisibility,
  visibleColumnsFromParent,
  columnOrder: columnOrderFromParent,
  onColumnOrderChange,
  accountCash = 0,  // Add a prop for account cash with default value 0
  onInitiateTransaction = null, // Add prop for transaction callback
  sortConfig, // Add prop for sort configuration
  onSortChange = null, // Add callback for sort changes
  tableLoading = false, // Add loading state prop
  onShieldUpdate = null, // Add callback for shield updates (receives optional tickerId)
  onTickerClick = null, // Add callback for ticker symbol clicks
  currentAccountId = null, // Add current account ID for context-aware ticker clicks
  onShowOrderDialog = null, // Add callback for opening order dialog
  hideOrderButton = false, // Add prop to control order button visibility
  onRefresh = null, // Add callback for refreshing data after manual price changes
  notesFloating = false, // Control notes popup floating/draggable behavior
  compactActions = false, // Render compact actions (+ / -) instead of full labels
  onQuickBuy = null, // Add callback for quick buy shortcut
  onQuickSell = null, // Add callback for quick sell shortcut
  quickTradeState = null, // Track quick trade loading state
  columnWidthOverrides = {}, // Optional per-column width overrides
  riskAmountPrecision = 0 // Controls fraction digits for Risk$ column
}, ref) => {
  const strategyCacheRef = useRef(new Map());
  const emphasizedLevelCacheRef = useRef(new Map()); // Cache for emphasized stop levels
  const effectivePercentageCacheRef = useRef(new Map()); // Cache for effective strategy percentages
  const shieldCacheRef = useRef({});
  const shieldCacheStorageKey = useMemo(() => {
    const baseAccountId = accountId || currentAccountId;
    return baseAccountId ? `tickerShieldsCache-${baseAccountId}` : null;
  }, [accountId, currentAccountId]);
  const isMountedRef = useRef(true);
  const strategyCacheStorageKey = useMemo(() => {
    const baseAccountId = accountId || currentAccountId;
    return baseAccountId ? `strategyBadgesCache-${baseAccountId}` : null;
  }, [accountId, currentAccountId]);

  const loadStrategyCacheFromStorage = useCallback((storageKey) => {
    if (!storageKey) return null;
    try {
      const cached = localStorage.getItem(storageKey);
      if (!cached) return null;
      const parsed = JSON.parse(cached);
      if (parsed && typeof parsed === 'object') {
        return parsed;
      }
    } catch (error) {
      console.warn('[TickerTable] Failed to load strategy cache from storage', error);
    }
    return null;
  }, []);

  const [strategyBadges, setStrategyBadges] = useState(() => {
    const stored = strategyCacheStorageKey ? loadStrategyCacheFromStorage(strategyCacheStorageKey) : null;
    if (stored) {
      Object.entries(stored).forEach(([key, value]) => {
        strategyCacheRef.current.set(key, value);
      });
      return stored;
    }
    return {};
  });

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // State for multi-account sorting mode - declare early to avoid hoisting issues
  const [multiSortMode, setMultiSortMode] = useState(() => {
    // Initialize from localStorage if available
    const savedMode = localStorage.getItem('multiSortMode');
    return savedMode === 'alphabetical' ? 'alphabetical' : 'grouped'; // Default to 'grouped'
  });

  useEffect(() => {
    strategyCacheRef.current.clear();
    if (shieldCacheStorageKey) {
      try {
        const cachedShields = localStorage.getItem(shieldCacheStorageKey);
        if (cachedShields) {
          const parsedShields = JSON.parse(cachedShields);
          if (parsedShields && typeof parsedShields === 'object') {
            shieldCacheRef.current = parsedShields;
          }
        }
      } catch (error) {
        console.warn('[TickerTable] Failed to load shield cache from storage', error);
      }
    } else {
      shieldCacheRef.current = {};
    }
    const stored = loadStrategyCacheFromStorage(strategyCacheStorageKey);
    if (stored) {
      Object.entries(stored).forEach(([key, value]) => {
        strategyCacheRef.current.set(key, value);
      });
      if (isMountedRef.current) {
        setStrategyBadges(stored);
      }
    } else if (isMountedRef.current) {
      setStrategyBadges({});
    }
  }, [loadStrategyCacheFromStorage, strategyCacheStorageKey, shieldCacheStorageKey]);

  useEffect(() => {
    if (!strategyCacheStorageKey) {
      return;
    }
    try {
      localStorage.setItem(strategyCacheStorageKey, JSON.stringify(strategyBadges));
    } catch (error) {
      console.warn('[TickerTable] Failed to persist strategy cache to storage', error);
    }
  }, [strategyBadges, strategyCacheStorageKey]);

  // Track strategy-relevant fields to detect when actual strategy data changes
  const tickersStrategySignature = useMemo(() => {
    if (!tickers || tickers.length === 0) return '';

    // Create a signature from strategy-relevant fields only
    // This way the cache is only cleared when these specific fields change
    return tickers.map(t => {
      const strategyFields = [
        t.ticker_id,
        t.strategy_label,
        t.strategy,
        t.strategy_preset,
        t.current_strategy,
        t.armor,
        // Include shield data if present
        t.shields ? JSON.stringify(t.shields) : ''
      ].join('|');
      return strategyFields;
    }).join('::');
  }, [tickers]);

  // Previous signature to detect changes
  const prevSignatureRef = useRef(null);

  // Clear strategy cache only when strategy-relevant fields change
  // This ensures that when data is updated in the database and fresh data is loaded,
  // the stale cached strategy values don't take precedence over the updated ticker fields
  useEffect(() => {
    if (prevSignatureRef.current === null) {
      // First render, just store the signature
      prevSignatureRef.current = tickersStrategySignature;
      return;
    }

    if (tickersStrategySignature !== prevSignatureRef.current && strategyCacheStorageKey) {
      logger.debug('[TickerTable] Strategy-related ticker fields changed, clearing strategy cache to force refresh');
      strategyCacheRef.current.clear();
      setStrategyBadges({});
      // Also clear from localStorage to ensure a full refresh
      try {
        localStorage.removeItem(strategyCacheStorageKey);
      } catch (error) {
        console.warn('[TickerTable] Failed to clear strategy cache from storage', error);
      }
      prevSignatureRef.current = tickersStrategySignature;
    }
  }, [tickersStrategySignature, strategyCacheStorageKey]);

  // Props to control whether to hide the multi-sorting toggle - declare early to avoid hoisting issues
  const [hideMultiSortToggle, setHideMultiSortToggle] = useState(false);

  const [sortColumn, setSortColumn] = useState(sortConfig?.key || 'symbol');
  const [sortDirection, setSortDirection] = useState(sortConfig?.direction || 'asc');
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [selectedTicker, setSelectedTicker] = useState(null);
  const [tickerNotesMap, setTickerNotesMap] = useState(() => ({})); // { tickerId: [notes] }
  const [tsizeEditorOpen, setTSizeEditorOpen] = useState(false);
  
  // Context menu and manual price modal state
  const [contextMenu, setContextMenu] = useState({ show: false, x: 0, y: 0, ticker: null });
  const [manualPriceModal, setManualPriceModal] = useState({ show: false, ticker: null });

  // Default Y position for floating Notes panel: align with top of top stats cards
  const notesDefaultY = React.useMemo(() => {
    if (!notesFloating) return null;
    if (typeof window === 'undefined' || typeof document === 'undefined') return null;
    const anchor = document.querySelector('.account-details-card');
    if (anchor && anchor.getBoundingClientRect) {
      const rect = anchor.getBoundingClientRect();
      return Math.max(20, Math.round(rect.top + window.scrollY));
    }
    return null;
  }, [notesFloating]);

  // Default X position for notes: align to the table's right edge with a small gap, not the screen edge
  const notesDefaultX = React.useMemo(() => {
    if (!notesFloating) return null;
    if (typeof window === 'undefined' || typeof document === 'undefined') return null;
    const tableEl = document.querySelector('.ticker-table');
    if (tableEl && tableEl.getBoundingClientRect) {
      const rect = tableEl.getBoundingClientRect();
      const gap = 12; // similar to spacing between table and actions row
      return Math.round(rect.right + window.scrollX + gap);
    }
    return null;
  }, [notesFloating]);
  const [editingTSizeTicker, setEditingTSizeTicker] = useState(null);
  const [nextEditorOpen, setNextEditorOpen] = useState(false);
  const [editingNextTicker, setEditingNextTicker] = useState(null);
  const [entryEditorOpen, setEntryEditorOpen] = useState(false);
  const [editingEntryTicker, setEditingEntryTicker] = useState(null);
  const [entrySaving, setEntrySaving] = useState(false);
  const [entryError, setEntryError] = useState('');
  
  // Update local sorting state when sortConfig prop changes
  useEffect(() => {
    if (sortConfig) {
      setSortColumn(sortConfig.key);
      setSortDirection(sortConfig.direction);
    }
  }, [sortConfig]);
  
  // Track expanded rows for multi-account tickers
  const [expandedRows, setExpandedRows] = useState(() => {
    // Initialize from localStorage if available
    const savedExpandedRows = localStorage.getItem('expandedRows');
    return savedExpandedRows ? JSON.parse(savedExpandedRows) : {};
  });
  // Define columns and their visibility state
  const [visibleColumns, setVisibleColumns] = useState({
    notes: true, // Ensure notes is always visible by default
    strategy: true,
    stop: true,
    name: true,
    rating: true,
    entry: false,
    tsize: true, // Target size column
    next: false,
    source: true,
    category: true,
    shares: true,
    cost_basis: true,
    total_cost: true,
    current_price: false,
    pre_close: true,
    current_value: true,
    high: true, // High column to show new highs since purchase
    today_gain_amount: true,
    today_gain_percentage: true,
    total_gain: true,
    total_gain_pct: true,
    trade_gain_pct: false,
    days_held: true,
    position_size: true,
    armor: true,
    risk_gain: true,
    trisk_gain: true, // Ensure trisk_gain is visible by default
    risk_gain_amount: true,
    theme: true,
    base: true,
    actions: true
  });

  // Use columns from parent if provided
  useEffect(() => {
    if (visibleColumnsFromParent && Object.keys(visibleColumnsFromParent).length > 0) {
      logger.debug('[TickerTable] Received visibleColumnsFromParent:', visibleColumnsFromParent);
      logger.debug('[TickerTable] Risk columns in parent:', {
        risk_gain: visibleColumnsFromParent.risk_gain,
        trisk_gain: visibleColumnsFromParent.trisk_gain,
        risk_gain_amount: visibleColumnsFromParent.risk_gain_amount
      });
      setVisibleColumns({ ...visibleColumnsFromParent });
    }
  }, [visibleColumnsFromParent]);
  
  // Debug logging for visibleColumns changes
  useEffect(() => {
    logger.debug('[TickerTable] visibleColumns changed:', visibleColumns);
    logger.debug('[TickerTable] Risk columns state:', {
      risk_gain: visibleColumns.risk_gain,
      trisk_gain: visibleColumns.trisk_gain,
      risk_gain_amount: visibleColumns.risk_gain_amount
    });
  }, [visibleColumns]);
  
  // Column definitions with headers for reuse
  const columnDefinitions = {
    symbol: { header: 'Sym', width: '75px', priority: 1, align: 'left' },
    strategy: { header: 'Strategy', width: '60px', priority: 1, align: 'center' },
    stop: { header: 'Stop', width: '75px', priority: 1, align: 'center' },
    notes: {
      header: 'Nts',
      width: 30,
      align: 'center',
    },
    actions: { header: 'Actions', width: compactActions ? '40px' : '60px', priority: 1, align: 'center' },
    name: { header: 'Name', width: '200px', priority: 1, align: 'center' },
    rating: { header: 'Rating', width: '100px', priority: 2, align: 'center' },
    entry: { header: 'Entry', width: '80px', priority: 2, align: 'center' },
    tsize: { header: 'TSz', width: '40px', priority: 2, align: 'center' },
    next: { header: 'Next', width: '120px', priority: 2, align: 'center' },
    source: { header: 'Source', width: '120px', priority: 2, align: 'center' },
    category: { header: 'Ctgry', width: '60px', priority: 2, align: 'center' },
    shares: { header: 'Shrs', width: '35px', priority: 1, align: 'center' },
    cost_basis: { header: 'Cost', width: '110px', priority: 2, align: 'center' },
    total_cost: { header: 'TCost', width: '110px', priority: 2, align: 'center' },
    current_price: { header: 'Price', width: '100px', priority: 1, align: 'center' },
    pre_close: { header: 'Prev Close', width: '110px', priority: 2, align: 'center' },
    current_value: { header: 'Value', width: 'auto', priority: 1, align: 'center' },
    high: { header: 'High', width: '25px', priority: 2, align: 'center' },
    today_gain_amount: { header: 'Tdy($)', width: '120px', priority: 2, align: 'center' },
    today_gain_percentage: { header: 'Tdy%', width: '70px', priority: 2, align: 'center' },
    total_gain: { header: 'Ttl($)', width: '120px', priority: 1, align: 'center' },
    total_gain_pct: { header: 'Ttl(%)', width: '70px', priority: 2, align: 'center' },
    unrealized_gain: { header: 'Unrlzd$', width: '120px', priority: 2, align: 'center' },
    trade_gain: { header: 'Trade$', width: 'auto', priority: 2, align: 'center' },
    trade_gain_pct: { header: 'Trade%', width: '70px', priority: 2, align: 'center' },
    days_held: { header: 'D', width: '40px', priority: 3, align: 'center' },
    position_size: { header: 'Size', width: '40px', priority: 2, align: 'center' },
    armor: { header: <i className="bi bi-shield-fill"></i>, width: '40px', priority: 3, align: 'center' },
    risk_gain: { header: 'Risk%', width: '70px', priority: 2, align: 'center' },
    trisk_gain: { header: 'TRisk%', width: '70px', priority: 2, align: 'center' },
    risk_gain_amount: { header: 'Risk$', width: '130px', priority: 2, align: 'center' },
    theme: { header: 'Thm', width: '70px', priority: 3, align: 'center' },
    base: { header: 'Base', width: '70px', priority: 3, align: 'center' }
  };

  // Function to toggle column visibility
  const toggleColumnVisibility = (column) => {
    // Don't allow toggling symbol column
    if (column === 'symbol') return;
    
    // Only toggle if we're not controlled by a parent
    if (!visibleColumnsFromParent) {
      setVisibleColumns(prev => ({
        ...prev,
        [column]: !prev[column]
      }));
    }
  };

  // Calculate dynamic column widths based on visible columns
  const calculateDynamicWidth = (column) => {
    const overrideWidth = columnWidthOverrides?.[column];
    if (overrideWidth) {
      return overrideWidth;
    }
    // Use predefined widths from columnDefinitions as base width
    const baseWidth = columnDefinitions[column]?.width || 'auto';
    
    // Fix: Ensure baseWidth is a string before calling indexOf
    if (typeof baseWidth === 'string' && baseWidth.indexOf('%') === -1) {
      return baseWidth;
    }
    if (typeof baseWidth === 'number') {
      return `${baseWidth}px`;
    }
    
    // Count visible columns to distribute available space
    const visibleColumnCount = Object.values(visibleColumns).filter(Boolean).length + 1; // +1 for symbol
    
    // Calculate relative width adjustment factors
    const totalAdjustmentFactor = visibleColumnCount <= 5 ? 0.2 : 
                                  visibleColumnCount <= 8 ? 0.15 : 0.1;
    
    // Give more space to high priority columns when fewer columns are visible
    const priority = columnDefinitions[column]?.priority || 2;
    const baseColumnPercent = 100 / visibleColumnCount;
    
    // Apply priority-based adjustments
    let adjustmentFactor = 0;
    switch(priority) {
      case 1: adjustmentFactor = totalAdjustmentFactor; break;
      case 2: adjustmentFactor = 0; break;
      case 3: adjustmentFactor = -totalAdjustmentFactor; break;
      default: adjustmentFactor = 0; break;
    }
    
    const widthPercent = baseColumnPercent * (1 + adjustmentFactor);
    
    // Apply content-specific adjustments for certain columns
    if (column === 'name') {
      // Name column typically needs more space for long text
      return `${Math.max(widthPercent * 1.2, 15)}%`;
    } else if (column === 'today_gain_amount' || column === 'today_gain_percentage' || 
               column === 'total_gain' || column === 'total_gain_pct') {
      // Gain columns with badges need a bit more space but less than before with shorter headers
      return `${Math.max(widthPercent * 1.05, 8)}%`;
    } else if (column === 'symbol') {
      // Symbol is typically short
      return `${Math.min(widthPercent * 0.8, 8)}%`;
    } else if (column === 'notes') {
      // Notes column only contains an icon, make it very narrow
      return `${Math.min(widthPercent * 0.4, 3)}%`;
    }
    
    return `${widthPercent}%`;
  };

  const renderHighStatusIcon = (isNewHigh, tooltipKey) => {
    if (isNewHigh === undefined || isNewHigh === null) {
      return (
        <span className="text-muted" title="High data unavailable">
          &mdash;
        </span>
      );
    }

    const iconColor = isNewHigh ? '#f5c518' : '#6c757d';
    const iconStyle = {
      color: iconColor,
      opacity: isNewHigh ? 1 : 0.45,
      fontSize: '1.1rem',
      filter: isNewHigh ? 'drop-shadow(0 0 4px rgba(245, 197, 24, 0.6))' : 'none',
      transition: 'transform 0.2s ease-in-out',
      transform: isNewHigh ? 'scale(1.05)' : 'scale(1)'
    };

    const tooltipText = isNewHigh
      ? 'New high recorded on the last trading day'
      : 'No new high on the last trading day';

    return (
      <OverlayTrigger
        placement="top"
        delay={{ show: 200, hide: 100 }}
        overlay={
          <Tooltip id={`high-status-${tooltipKey}`}>
            {tooltipText}
          </Tooltip>
        }
        trigger={["hover", "focus"]}
      >
        <span
          className="d-inline-flex align-items-center justify-content-center"
          style={{ cursor: 'default', width: '100%' }}
        >
          <FaStar
            aria-label={isNewHigh ? 'New high' : 'No new high'}
            style={iconStyle}
          />
        </span>
      </OverlayTrigger>
    );
  };

  // Get column's style based on visibility
  const getColumnStyle = (column) => {
    const baseStyle = { 
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      textAlign: 'left'
    };
    
    if (column === 'symbol' || visibleColumns[column]) {
      // Get alignment from column definitions if specified
      const columnDef = columnDefinitions[column];
      const textAlign = columnDef?.align || 'left';
      const overrideWidth = columnWidthOverrides?.[column];
      if (overrideWidth) {
        return {
          ...baseStyle,
          textAlign,
          width: overrideWidth,
          minWidth: overrideWidth
        };
      }
      
      let minWidth = '80px'; // Default minimum width
      if (column === 'symbol') minWidth = '75px';
      else if (column === 'strategy') minWidth = '110px';
      else if (column === 'stop') minWidth = '75px';
      else if (column === 'actions') minWidth = compactActions ? '36px' : '60px';
      else if (column === 'notes') minWidth = '75px';
      else if (column === 'high') minWidth = '25px';
      else if (column === 'today_gain_percentage') minWidth = '70px';
      else if (column === 'total_gain_pct') minWidth = '70px';
      else if (column === 'trade_gain_pct') minWidth = '70px';
      else if (column === 'days_held') minWidth = '40px';
      else if (column === 'position_size') minWidth = '40px';
      else if (column === 'tsize') minWidth = '36px';
      else if (column === 'shares') minWidth = '35px';
      else if (column === 'armor') minWidth = '40px';
      else if (column === 'today_gain_amount') minWidth = '120px';
      else if (column === 'total_gain') minWidth = '120px';
      else if (column === 'unrealized_gain') minWidth = '80px';
      else if (column === 'current_value') minWidth = '70px';
      else if (column === 'trade_gain') minWidth = '70px';
      else if (column === 'risk_gain') minWidth = '70px';
      else if (column === 'name') minWidth = '150px';
      else if (column === 'category') minWidth = '60px';

      const style = {
        ...baseStyle,
        textAlign: textAlign,
        width: calculateDynamicWidth(column),
        minWidth: minWidth
      };

      // Add maxWidth constraint for flexible currency columns
      if (column === 'current_value' || column === 'trade_gain') {
        style.maxWidth = '100px';
      }

      return style;
    }
    
    return { ...baseStyle, display: 'none' };
  };

  // Handle drag start
  const handleDragStart = (e, column) => {
    if (column === 'symbol' || column === 'strategy' || column === 'stop' || column === 'actions') return; // Prevent dragging the symbol, strategy, stop, and actions columns
    setDraggedColumn(column);
    e.dataTransfer.effectAllowed = 'move';
    // Setting some dummy data to make drag work in all browsers
    e.dataTransfer.setData('text/plain', column);
  };

  // Handle drag over
  const handleDragOver = (e, column) => {
    e.preventDefault();
    if (column === 'symbol' || column === 'strategy' || column === 'stop' || column === 'actions' || !draggedColumn || draggedColumn === column) {
      setDragOverColumn(null);
      setDragPosition(null);
      return;
    }
    
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(column);
    
    // Determine if we're on the left or right side of the column
    const rect = e.currentTarget.getBoundingClientRect();
    const mouseX = e.clientX;
    const columnMiddle = rect.left + (rect.width / 2);
    
    // Set drag position based on mouse position relative to column center
    setDragPosition(mouseX < columnMiddle ? 'left' : 'right');
  };

  // Handle drag leave
  const handleDragLeave = () => {
    setDragOverColumn(null);
    setDragPosition(null);
  };

  // Handle drop
  const handleDrop = (e, targetColumn) => {
    e.preventDefault();
    if (targetColumn === 'symbol' || targetColumn === 'strategy' || targetColumn === 'stop' || !draggedColumn || draggedColumn === targetColumn) {
      setDragOverColumn(null);
      setDragPosition(null);
      return;
    }
    
    // Reorder columns based on drop position
    const newOrder = [...columnOrder];
    const draggedIndex = newOrder.indexOf(draggedColumn);
    let targetIndex = newOrder.indexOf(targetColumn);
    
    // Adjust target index based on drop position
    if (dragPosition === 'right') {
      targetIndex++; // Place after the target column
    }
    
    if (draggedIndex !== -1 && targetIndex !== -1) {
      newOrder.splice(draggedIndex, 1);
      // Adjust target index if we removed an item that affects its position
      if (draggedIndex < targetIndex) {
        targetIndex--;
      }
      newOrder.splice(targetIndex, 0, draggedColumn);
      updateColumnOrder(newOrder);
    }
    
    // Reset drag state
    setDraggedColumn(null);
    setDragOverColumn(null);
    setDragPosition(null);
  };

  // Handle drag end
  const handleDragEnd = () => {
    setDraggedColumn(null);
    setDragOverColumn(null);
    setDragPosition(null);
  };

  // Move column left
  const moveColumnLeft = (column) => {
    if (column === 'symbol' || column === 'strategy' || column === 'stop' || column === 'actions') return; // Cannot move fixed columns

    const index = columnOrder.indexOf(column);
    if (index <= 3) return; // Do not move into the fixed column positions

    const newOrder = [...columnOrder];
    [newOrder[index], newOrder[index - 1]] = [newOrder[index - 1], newOrder[index]];
    updateColumnOrder(newOrder);
  };

  // Move column right
  const moveColumnRight = (column) => {
    if (column === 'symbol' || column === 'strategy' || column === 'stop' || column === 'actions') return; // Cannot move fixed columns

    const index = columnOrder.indexOf(column);
    if (index === -1 || index === columnOrder.length - 1) return;
    
    const newOrder = [...columnOrder];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    updateColumnOrder(newOrder);
  };


  // Handle column header click
  const handleHeaderClick = (column) => {
    // Only sort if the column is valid for sorting
    if (column) {
      const newDirection = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc';
      setSortColumn(column);
      setSortDirection(newDirection);
      
      // Call the onSortChange callback if provided
      if (onSortChange) {
        onSortChange(column, newDirection);
      }
    }
  };

  // Render sort indicator
  const renderSortIndicator = (column) => {
    if (sortColumn === column) {
      return (
        <span style={{ marginLeft: '4px', fontSize: '0.85em' }}>
          {sortDirection === 'asc' ? '▲' : '▼'}
        </span>
      );
    }
    return '';
  };

  // Function to format currency values
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  };

  // Function to format currency values without decimals (whole dollars)
  const formatCurrencyWhole = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const safeRiskPrecision = useMemo(() => (
    typeof riskAmountPrecision === 'number' && riskAmountPrecision >= 0
      ? riskAmountPrecision
      : 2
  ), [riskAmountPrecision]);

  const riskCurrencyFormatter = useMemo(() => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: safeRiskPrecision,
    maximumFractionDigits: safeRiskPrecision
  }), [safeRiskPrecision]);

  const formatRiskAmount = (value) => {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return '-';
    }
    return riskCurrencyFormatter.format(value);
  };

  // Function to format number with commas
  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  // Function to format percentage values
  const formatPercentage = (value) => {
    if (value === undefined || value === null) return '0.00%';
    // Treat very small values (close to zero) as zero
    const absValue = Math.abs(value);
    if (absValue < 0.01) {
      return '0.00%';
    }
    return `${value.toFixed(2)}%`;
  };

  // Function to determine color based on positive/negative value
  const getVariant = (value) => {
    // Treat very small values (close to zero) as zero
    const absValue = Math.abs(value);
    if (absValue < 0.01) {
      return 'success'; // Show green for values close to zero
    }
    if (value > 0) return 'success';
    if (value < 0) return 'danger';
    return 'secondary';
  };

  // Function to calculate position size
  const calculatePositionSize = useCallback((currentValue) => {
    if (!accountStandardSize || accountStandardSize === 0) return null;
    return currentValue / accountStandardSize;
  }, [accountStandardSize]);

  const parseNumericValue = useCallback((value) => {
    if (value === null || value === undefined) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }, []);

  const getCurrentTradeInfo = useCallback((ticker) => {
    if (!ticker) {
      return { tradeId: null, gainPct: null };
    }

    const tradeSource = ticker.current_trade || {};
    const rawTradeId = ticker.current_trade_id ?? tradeSource.trade_id;
    const rawGainPct = ticker.current_trade_gain_pct ?? tradeSource.total_gain_loss_pct;

    let tradeId = null;
    if (rawTradeId !== null && rawTradeId !== undefined) {
      const parsedId = Number(rawTradeId);
      tradeId = Number.isFinite(parsedId) ? parsedId : rawTradeId;
    }

    return {
      tradeId,
      gainPct: parseNumericValue(rawGainPct)
    };
  }, [parseNumericValue]);

  // Derive a dollar gain/loss for the active trade, using accumulated trade gain
  const getTradeGainAmount = useCallback((ticker, gainPctOverride = null) => {
    if (!ticker) {
      return null;
    }

    // First, try to get the accumulated trade gain/loss from current_trade
    const tradeSource = ticker.current_trade || {};
    const tradeGainLoss = parseNumericValue(ticker.current_trade_gain_loss)
                       ?? parseNumericValue(tradeSource.total_gain_loss)
                       ?? parseNumericValue(tradeSource.final_gain_loss);

    if (tradeGainLoss !== null) {
      return tradeGainLoss;
    }

    // Fallback to position-based calculations if trade data not available
    const totalGain = parseNumericValue(ticker.total_gain);
    if (totalGain !== null) {
      return totalGain;
    }

    const shares = parseNumericValue(ticker.shares);
    const costBasis = parseNumericValue(ticker.cost_basis);
    const currentValue = parseNumericValue(ticker.current_value);
    const totalCost = parseNumericValue(ticker.total_cost) ?? (
      shares !== null && costBasis !== null ? shares * costBasis : null
    );

    if (currentValue !== null && totalCost !== null) {
      return currentValue - totalCost;
    }

    const gainPct = gainPctOverride !== null && gainPctOverride !== undefined
      ? parseNumericValue(gainPctOverride)
      : parseNumericValue(ticker.total_gain_pct);

    if (gainPct !== null && totalCost !== null) {
      return (gainPct / 100) * totalCost;
    }

    return null;
  }, [parseNumericValue]);

  // Function to get risk data from stored values, with fallback for amount
  const getRiskGain = (ticker, accountData = null) => {
    // Use account data if provided (for instance rows), otherwise use ticker data
    const tickerData = accountData || ticker;

    // Prefer backend/ArmorEdit-provided risk amount when available (most accurate)
    const providedAmount = tickerData.shield_risk_amount;
    const percentage = tickerData.shield_risk_percentage;

    // If we have a percentage, compute a robust amount value
    if (percentage !== undefined && percentage !== null) {
      // Use provided amount if present; otherwise derive from current shares and cost basis
      let amount = providedAmount;
      if (amount === undefined || amount === null) {
        const shares = (tickerData.shares ?? ticker.shares) || 0;
        const costBasisPerShare = (tickerData.cost_basis ?? ticker.cost_basis) || 0;
        const costBasisPositionValue = shares * costBasisPerShare;
        amount = costBasisPositionValue > 0 ? (percentage / 100) * costBasisPositionValue : null;
      }

      return {
        value: percentage,
        isProfit: percentage > 0,
        amount
      };
    }

    // If no stored risk data available, return null values
    return { value: null, isProfit: false, amount: null };
  };

  // Function to calculate aggregated risk for multi-account tickers
  const getAggregatedRiskGain = (ticker) => {
    if (!ticker.accounts || ticker.accounts.length === 0) {
      return { value: null, isProfit: false, amount: null };
    }

    let totalRiskAmount = 0;
    let totalCostBasis = 0;
    let hasAnyRiskData = false;

    // Sum up risk amounts and cost basis from all accounts
    ticker.accounts.forEach(account => {
      const shares = account.shares || 0;
      const costBasisPerShare = account.cost_basis || 0;
      const accountCostBasis = shares * costBasisPerShare;

      totalCostBasis += accountCostBasis;

      // Check if this account has risk data
      if (account.shield_risk_percentage !== undefined && account.shield_risk_percentage !== null) {
        hasAnyRiskData = true;

        // Use provided risk amount or calculate from percentage
        let accountRiskAmount = account.shield_risk_amount;
        if (accountRiskAmount === undefined || accountRiskAmount === null) {
          accountRiskAmount = accountCostBasis > 0 ? (account.shield_risk_percentage / 100) * accountCostBasis : 0;
        }

        totalRiskAmount += accountRiskAmount;
      }
    });

    // If no accounts have risk data, return null
    if (!hasAnyRiskData) {
      return { value: null, isProfit: false, amount: null };
    }

    // Calculate aggregated percentage as weighted average
    const aggregatedPercentage = totalCostBasis > 0 ? (totalRiskAmount / totalCostBasis) * 100 : null;

    return {
      value: aggregatedPercentage,
      isProfit: aggregatedPercentage !== null && aggregatedPercentage > 0,
      amount: totalRiskAmount
    };
  };

  const getAccountContextId = useCallback((ticker) => {
    if (accountId !== undefined && accountId !== null) {
      return accountId;
    }

    if (currentAccountId !== undefined && currentAccountId !== null) {
      return currentAccountId;
    }

    if (ticker?.account_id !== undefined && ticker?.account_id !== null) {
      return ticker.account_id;
    }

    if (Array.isArray(ticker?.accounts) && ticker.accounts.length === 1) {
      const onlyAccount = ticker.accounts[0];
      if (onlyAccount && onlyAccount.account_id !== undefined && onlyAccount.account_id !== null) {
        return onlyAccount.account_id;
      }
    }

    return null;
  }, [accountId, currentAccountId]);

  const getStrategyCacheKey = useCallback((ticker) => {
    if (!ticker || ticker.ticker_id === undefined || ticker.ticker_id === null) {
      return null;
    }

    const baseKey = String(ticker.ticker_id);
    const accountContextId = getAccountContextId(ticker);

    if (accountContextId === undefined || accountContextId === null) {
      return baseKey;
    }

    return `${baseKey}:${accountContextId}`;
  }, [getAccountContextId]);

  const determineStrategyForTicker = useCallback(async (ticker, { force = false } = {}) => {
    if (!ticker || ticker.ticker_id === undefined || ticker.ticker_id === null) {
      return { tickerId: ticker?.ticker_id ?? null, cacheKey: null, strategyKey: null };
    }

    const tickerId = ticker.ticker_id;
    const cacheKey = getStrategyCacheKey(ticker);

    if (!force && cacheKey && strategyCacheRef.current.has(cacheKey)) {
      return { tickerId, cacheKey, strategyKey: strategyCacheRef.current.get(cacheKey) };
    }

    const directKey = deriveStrategyKeyFromTickerFields(ticker);
    const inlineShieldKey = deriveStrategyKeyFromTickerShields(ticker);

    if (!force && inlineShieldKey) {
      if (cacheKey) {
        strategyCacheRef.current.set(cacheKey, inlineShieldKey);
      }
      return { tickerId, cacheKey, strategyKey: inlineShieldKey };
    }

    if (!force && directKey) {
      if (cacheKey) {
        strategyCacheRef.current.set(cacheKey, directKey);
      }
      return { tickerId, cacheKey, strategyKey: directKey };
    }

    const armorValue = typeof ticker.armor === 'string' ? ticker.armor.trim().toLowerCase() : '';
    const expectsShields = armorValue && armorValue !== 'no armor';

    if (!force && !expectsShields) {
      const resolvedKey = directKey || inlineShieldKey || null;
      if (cacheKey) {
        strategyCacheRef.current.set(cacheKey, resolvedKey);
      }
      return { tickerId, cacheKey, strategyKey: resolvedKey };
    }

    const accountContextId = getAccountContextId(ticker);

    if (!accountContextId) {
      const resolvedKey = directKey || inlineShieldKey || null;
      if (cacheKey) {
        strategyCacheRef.current.set(cacheKey, resolvedKey);
      }
      return { tickerId, cacheKey, strategyKey: resolvedKey };
    }

    try {
      let shields = shieldCacheRef.current[accountContextId]?.[tickerId];
      if (!shields) {
        const response = await getTickerShields(tickerId, accountContextId);
        shields = response?.data?.shields || [];
        shieldCacheRef.current[accountContextId] = shieldCacheRef.current[accountContextId] || {};
        shieldCacheRef.current[accountContextId][tickerId] = shields;
        if (shieldCacheStorageKey) {
          try {
            localStorage.setItem(shieldCacheStorageKey, JSON.stringify(shieldCacheRef.current));
          } catch (error) {
            console.warn('[TickerTable] Failed to persist shield cache to storage', error);
          }
        }
      }
      const shieldKey = deriveStrategyKeyFromShields(shields);
      const resolvedKey = shieldKey || directKey || inlineShieldKey || null;

      // Cache the emphasized level and effective percentage for all-in strategies
      if (cacheKey && resolvedKey && ['T2-5-9', 'T3-7-12', 'T5-10-15'].includes(resolvedKey)) {
        const emphasizedLevel = getEmphasizedStopLevelFromShields(shields, resolvedKey);
        if (emphasizedLevel) {
          emphasizedLevelCacheRef.current.set(cacheKey, emphasizedLevel);
        }
        const effectivePercentage = getEffectiveStrategyPercentage(shields, resolvedKey);
        if (effectivePercentage !== null) {
          effectivePercentageCacheRef.current.set(cacheKey, effectivePercentage);
        }
      }

      if (cacheKey) {
        strategyCacheRef.current.set(cacheKey, resolvedKey);
      }
      return { tickerId, cacheKey, strategyKey: resolvedKey };
    } catch (error) {
      logger.warn(`[TickerTable] Failed to load shields for ticker ${ticker.symbol || tickerId}`, error);
      const resolvedKey = directKey || inlineShieldKey || null;
      if (cacheKey) {
        strategyCacheRef.current.set(cacheKey, resolvedKey);
      }
      return { tickerId, cacheKey, strategyKey: resolvedKey };
    }
  }, [getAccountContextId, getStrategyCacheKey, shieldCacheStorageKey]);

  useEffect(() => {
    if (!Array.isArray(tickers) || tickers.length === 0) {
      return;
    }

    let cancelled = false;

    const loadStrategies = async () => {
      try {
        const nextKeys = new Set();
        const fetchQueue = [];

        tickers.forEach((ticker) => {
          const cacheKey = getStrategyCacheKey(ticker);
          if (!cacheKey) {
            return;
          }

          nextKeys.add(cacheKey);

          const inlineKey = deriveStrategyKeyFromTickerShields(ticker) || deriveStrategyKeyFromTickerFields(ticker);

          if (inlineKey) {
            strategyCacheRef.current.set(cacheKey, inlineKey);
            return;
          }

          if (!strategyCacheRef.current.has(cacheKey)) {
            fetchQueue.push(ticker);
          }
        });

        strategyCacheRef.current.forEach((_, key) => {
          if (!nextKeys.has(key)) {
            strategyCacheRef.current.delete(key);
          }
        });

        if (!cancelled && isMountedRef.current) {
          const snapshot = {};
          nextKeys.forEach((key) => {
            if (strategyCacheRef.current.has(key)) {
              snapshot[key] = strategyCacheRef.current.get(key);
            }
          });
          setStrategyBadges(snapshot);
        }

        for (const ticker of fetchQueue) {
          if (cancelled || !isMountedRef.current) {
            break;
          }

          try {
            const { cacheKey, strategyKey } = await determineStrategyForTicker(ticker, { force: true });

            if (!cacheKey) {
              continue;
            }

            const resolvedKey = strategyKey ?? null;
            strategyCacheRef.current.set(cacheKey, resolvedKey);

            if (cancelled || !isMountedRef.current) {
              continue;
            }

            setStrategyBadges((prev) => {
              if (prev[cacheKey] === resolvedKey) {
                return prev;
              }
              return { ...prev, [cacheKey]: resolvedKey };
            });
          } catch (error) {
            logger.warn('[TickerTable] Failed to determine strategy badge for ticker', error);
          }
        }
      } catch (error) {
        logger.warn('[TickerTable] Failed to determine strategy badges', error);
      }
    };

    loadStrategies();

    return () => {
      cancelled = true;
    };
  }, [tickers, determineStrategyForTicker, getStrategyCacheKey]);

  const refreshStrategyBadge = useCallback((targetTickerId) => {
    if (targetTickerId === undefined || targetTickerId === null) {
      return;
    }

    const target = tickers.find((ticker) => ticker?.ticker_id === targetTickerId);

    if (!target) {
      return;
    }

    const cacheKey = getStrategyCacheKey(target);
    const accountContextId = getAccountContextId(target);

    if (cacheKey) {
      strategyCacheRef.current.delete(cacheKey);
      emphasizedLevelCacheRef.current.delete(cacheKey); // Also clear emphasized level cache
      effectivePercentageCacheRef.current.delete(cacheKey); // Also clear effective percentage cache
    }

    if (accountContextId && shieldCacheRef.current[accountContextId]) {
      delete shieldCacheRef.current[accountContextId][targetTickerId];
      if (Object.keys(shieldCacheRef.current[accountContextId]).length === 0) {
        delete shieldCacheRef.current[accountContextId];
      }
      if (shieldCacheStorageKey) {
        try {
          localStorage.setItem(shieldCacheStorageKey, JSON.stringify(shieldCacheRef.current));
        } catch (error) {
          console.warn('[TickerTable] Failed to persist shield cache after refresh', error);
        }
      }
    }

    determineStrategyForTicker(target, { force: true })
      .then(({ strategyKey }) => {
        if (!isMountedRef.current) {
          return;
        }

        setStrategyBadges((prev) => {
          const key = strategyKey ?? null;
          if (!cacheKey) {
            return prev;
          }

          if (prev[cacheKey] === key) {
            return prev;
          }
          return { ...prev, [cacheKey]: key };
        });
      })
      .catch((error) => {
        logger.warn(`[TickerTable] Failed to refresh strategy badge for ticker ${target.symbol || targetTickerId}`, error);
      });
  }, [determineStrategyForTicker, getAccountContextId, getStrategyCacheKey, shieldCacheStorageKey, tickers]);

  const handleCategoryUpdate = (updatedTicker) => {
    if (onTickerUpdate) {
      // Update the ticker in the local state with the new category data
      const updatedTickers = tickers.map(ticker => {
        if (ticker.ticker_id === updatedTicker.ticker_id) {
          return {
            ...ticker,
            category: {
              category_id: updatedTicker.category_id,
              name: updatedTicker.category_name
            }
          };
        }
        return ticker;
      });
      onTickerUpdate(updatedTickers);
    }
  };

  const handleThemeToggle = async (ticker) => {
    try {
      // Check if this is a multi-account ticker
      const isMultiAccount = ticker.accounts && ticker.accounts.length > 0;

      if (isMultiAccount) {
        // For multi-account tickers, toggle all accounts to the opposite of the aggregated state
        const aggregatedState = getAggregatedThemeState(ticker);
        const newThemeEnabled = !aggregatedState;

        // Update all accounts
        const updatePromises = ticker.accounts.map(account =>
          updateAccountTickerTheme(account.account_id, ticker.ticker_id, newThemeEnabled)
            .then(() => {
              setThemeOverride(account.account_id, ticker.ticker_id, newThemeEnabled);
            })
        );

        await Promise.all(updatePromises);

        if (onTickerUpdate) {
          const updatedTickers = tickers.map(t => {
            if (t.ticker_id !== ticker.ticker_id) {
              return t;
            }

            let nextTicker = { ...t };

            // Update all accounts in the accounts array
            if (Array.isArray(t.accounts) && t.accounts.length > 0) {
              nextTicker.accounts = t.accounts.map(accountEntry => ({
                ...accountEntry,
                theme_enabled: newThemeEnabled
              }));
            }

            // Update the top-level theme_enabled to match aggregated state
            nextTicker.theme_enabled = newThemeEnabled;

            return nextTicker;
          });
          onTickerUpdate(updatedTickers);
        }
      } else {
        // Single account ticker - original logic
        const newThemeEnabled = !ticker.theme_enabled;
        const targetAccountId = ticker.account_id ?? accountId ?? currentAccountId;
        if (!targetAccountId) {
          console.warn('Missing account_id for theme toggle', ticker);
          return;
        }
        await updateAccountTickerTheme(targetAccountId, ticker.ticker_id, newThemeEnabled);

        setThemeOverride(targetAccountId, ticker.ticker_id, newThemeEnabled);

        const normalizedTickerAccountId = ticker.account_id ?? targetAccountId;

        if (onTickerUpdate) {
          const updatedTickers = tickers.map(t => {
            if (t.ticker_id !== ticker.ticker_id) {
              return t;
            }

            const accountsMatch = (
              t.account_id === undefined || normalizedTickerAccountId === undefined
            ) ? true : String(t.account_id) === String(normalizedTickerAccountId);

            let nextTicker = t;

            if (accountsMatch && t.theme_enabled !== newThemeEnabled) {
              nextTicker = { ...t, theme_enabled: newThemeEnabled };
            }

            if (Array.isArray(t.accounts) && t.accounts.length > 0) {
              let accountsChanged = false;
              const updatedAccounts = t.accounts.map(accountEntry => {
                if (String(accountEntry.account_id) === String(normalizedTickerAccountId)) {
                  if (accountEntry.theme_enabled === newThemeEnabled) {
                    return accountEntry;
                  }
                  accountsChanged = true;
                  return { ...accountEntry, theme_enabled: newThemeEnabled };
                }
                return accountEntry;
              });

              if (accountsChanged) {
                nextTicker = nextTicker === t ? { ...t } : nextTicker;
                nextTicker.accounts = updatedAccounts;
              }
            }

            return nextTicker;
          });
          onTickerUpdate(updatedTickers);
        } else {
          console.warn('onTickerUpdate is not defined');
        }
      }
    } catch (error) {
      console.error('Error updating theme status:', error);
      console.error('Error details:', error.response?.data || error.message);
    }
  };

  const handleBaseToggle = async (ticker) => {
    try {
      // Check if this is a multi-account ticker
      const isMultiAccount = ticker.accounts && ticker.accounts.length > 0;

      if (isMultiAccount) {
        // For multi-account tickers, toggle all accounts to the opposite of the aggregated state
        const aggregatedState = getAggregatedBaseState(ticker);
        const newBaseEnabled = !aggregatedState;

        // Update all accounts
        const updatePromises = ticker.accounts.map(account =>
          updateAccountTickerBase(account.account_id, ticker.ticker_id, newBaseEnabled)
            .then(() => {
              setBaseOverride(account.account_id, ticker.ticker_id, newBaseEnabled);
            })
        );

        await Promise.all(updatePromises);

        if (onTickerUpdate) {
          const updatedTickers = tickers.map(t => {
            if (t.ticker_id !== ticker.ticker_id) {
              return t;
            }

            let nextTicker = { ...t };

            // Update all accounts in the accounts array
            if (Array.isArray(t.accounts) && t.accounts.length > 0) {
              nextTicker.accounts = t.accounts.map(accountEntry => ({
                ...accountEntry,
                base_enabled: newBaseEnabled
              }));
            }

            // Update the top-level base_enabled to match aggregated state
            nextTicker.base_enabled = newBaseEnabled;

            return nextTicker;
          });
          onTickerUpdate(updatedTickers);
        }
      } else {
        // Single account ticker - original logic
        const newBaseEnabled = !ticker.base_enabled;
        const targetAccountId = ticker.account_id ?? accountId ?? currentAccountId;
        if (!targetAccountId) {
          console.warn('Missing account_id for base toggle', ticker);
          return;
        }
        await updateAccountTickerBase(targetAccountId, ticker.ticker_id, newBaseEnabled);

        setBaseOverride(targetAccountId, ticker.ticker_id, newBaseEnabled);

        const normalizedTickerAccountId = ticker.account_id ?? targetAccountId;

        if (onTickerUpdate) {
          const updatedTickers = tickers.map(t => {
            if (t.ticker_id !== ticker.ticker_id) {
              return t;
            }

            const accountsMatch = (
              t.account_id === undefined || normalizedTickerAccountId === undefined
            ) ? true : String(t.account_id) === String(normalizedTickerAccountId);

            let nextTicker = t;

            if (accountsMatch && t.base_enabled !== newBaseEnabled) {
              nextTicker = { ...t, base_enabled: newBaseEnabled };
            }

            if (Array.isArray(t.accounts) && t.accounts.length > 0) {
              let accountsChanged = false;
              const updatedAccounts = t.accounts.map(accountEntry => {
                if (String(accountEntry.account_id) === String(normalizedTickerAccountId)) {
                  if (accountEntry.base_enabled === newBaseEnabled) {
                    return accountEntry;
                  }
                  accountsChanged = true;
                  return { ...accountEntry, base_enabled: newBaseEnabled };
                }
                return accountEntry;
              });

              if (accountsChanged) {
                nextTicker = nextTicker === t ? { ...t } : nextTicker;
                nextTicker.accounts = updatedAccounts;
              }
            }

            return nextTicker;
          });
          onTickerUpdate(updatedTickers);
        } else {
          console.warn('onTickerUpdate is not defined');
        }
      }
    } catch (error) {
      console.error('Error updating base status:', error);
      console.error('Error details:', error.response?.data || error.message);
    }
  };

  // Function to fetch shield data and calculate risk values
  // const calculateShieldRisk = async (tickerId, accountIdForShields) => { ... }

  const handleArmorUpdate = async (updatedTicker) => {
    // Debug logging for armor updates
    logger.debug(`[TickerTable] ${new Date().toISOString()} - Received armor update for ticker ${updatedTicker.ticker_id}:`, {
      armor: updatedTicker.armor,
      shield_risk_percentage: updatedTicker.shield_risk_percentage,
      shield_risk_amount: updatedTicker.shield_risk_amount
    });
    
    // Check if the ticker already has the same values to avoid unnecessary re-renders
    const existingTicker = tickers.find(t => t.ticker_id === updatedTicker.ticker_id);
    if (existingTicker && 
        existingTicker.shield_risk_percentage === updatedTicker.shield_risk_percentage &&
        existingTicker.shield_risk_amount === updatedTicker.shield_risk_amount &&
        existingTicker.armor === updatedTicker.armor) {
      logger.debug(`[TickerTable] Skipping update for ticker ${updatedTicker.ticker_id} - values unchanged`);
      return;
    }
    
    // Update the parent component to ensure risk columns persist and let the sorting logic handle the re-sort
    if (onTickerUpdate) {
      const updatedTickers = tickers.map(ticker => {
        if (ticker.ticker_id === updatedTicker.ticker_id) {
          return {
            ...ticker,
            armor: updatedTicker.armor,
            shield_risk_percentage: updatedTicker.shield_risk_percentage,
            shield_risk_amount: updatedTicker.shield_risk_amount
          };
        }
        return ticker;
      });
      onTickerUpdate(updatedTickers);
    }
  };

  const handleShieldRefresh = useCallback((updatedTickerId = null) => {
    if (onShieldUpdate) {
      onShieldUpdate(updatedTickerId);
    }

    if (updatedTickerId !== undefined && updatedTickerId !== null) {
      refreshStrategyBadge(updatedTickerId);
      return;
    }

    if (!Array.isArray(tickers) || tickers.length === 0) {
      return;
    }

    strategyCacheRef.current.clear();
    if (isMountedRef.current) {
      setStrategyBadges({});
    }

    tickers.forEach((ticker) => {
      if (ticker?.ticker_id !== undefined && ticker?.ticker_id !== null) {
        refreshStrategyBadge(ticker.ticker_id);
      }
    });
  }, [onShieldUpdate, refreshStrategyBadge, tickers]);

  // Get additional classes for header based on drag state
  const getHeaderDragClasses = (column) => {
    const classes = [];
    
    if (column === draggedColumn) {
      classes.push('dragging');
    }
    
    if (column === dragOverColumn) {
      classes.push('drag-over');
      if (dragPosition === 'left') {
        classes.push('drag-over-left');
      } else if (dragPosition === 'right') {
        classes.push('drag-over-right');
      }
    }
    
    return classes.join(' ');
  };

  // Helper to render table header
  const renderTableHeader = (column) => {
    // Add a function to check if a column has multi-account tickers
    const hasMultiAccountTickers = () => {
      return tickers.some(ticker => ticker.in_multiple_accounts === true ||
        (ticker.accounts && ticker.accounts.length > 1));
    };
    
    // Show info about multi-sorting mode if sorting by symbol and we have multi-account tickers
    const showMultiSortInfo = column === 'symbol' && hasMultiAccountTickers() && 
      !hideMultiSortToggle && multiSortMode === 'grouped';
    
    if (column === 'symbol' || visibleColumns[column]) {
      // Special style for actions column to center align
      const headerStyle = column === 'actions' || column === 'strategy' || column === 'stop' || column === 'armor' || column === 'notes' || column === 'rating' || column === 'entry'
        ? { ...getColumnStyle(column), textAlign: 'center' }
        : column === 'shares' || column === 'cost_basis' || column === 'total_cost' ||
          column === 'current_price' || column === 'pre_close' || column === 'current_value' ||
          column === 'today_gain_amount' || column === 'today_gain_percentage' ||
          column === 'total_gain' || column === 'total_gain_pct' || column === 'unrealized_gain' || column === 'trade_gain' || column === 'trade_gain_pct' || column === 'position_size' ||
          column === 'risk_gain' || column === 'risk_gain_amount' || column === 'days_held' ||
          column === 'category' || column === 'source' || column === 'tsize' || column === 'next'
        ? { ...getColumnStyle(column), textAlign: 'right' }
        : getColumnStyle(column);
        
      return (
        <th 
          key={column}
          style={headerStyle}
          onClick={() => handleHeaderClick(column)}
          className={`tickers-table-header ${sortColumn === column ? 'active-sort' : ''} ${getHeaderDragClasses(column)}`}
          draggable={column !== 'symbol' && column !== 'strategy' && column !== 'actions'}
          onDragStart={(e) => handleDragStart(e, column)}
          onDragOver={(e) => handleDragOver(e, column)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, column)}
          onDragEnd={handleDragEnd}
          title={`${columnDefinitions[column]?.header} - Click to sort, drag to reorder${column === 'symbol' && multiSortMode === 'grouped' ? ' (Multi-account tickers are grouped)' : ''}`}
        >
          {columnDefinitions[column]?.header}
          {renderSortIndicator(column)}
          {showMultiSortInfo && (
            <Button 
              variant="outline-primary" 
              size="sm" 
              className="ms-2" 
              onClick={(e) => {
                e.stopPropagation(); // Prevent sort when clicking the toggle
                toggleMultiSortMode();
              }}
              title={`Currently grouping multi-account tickers. Click to sort alphabetically.`}
            >
              Grouped
            </Button>
          )}
        </th>
      );
    }
    
    return null;
  };
  
  const normalizeSymbol = (value) => {
    if (!value) {
      return '';
    }

    return String(value).toUpperCase();
  };

  const isQuickTradeActionDisabled = (ticker, action) => {
    if (!quickTradeState || !quickTradeState.symbol || !quickTradeState.type) {
      return false;
    }

    if (quickTradeState.type !== action) {
      return false;
    }

    const tickerSymbol = normalizeSymbol(ticker?.symbol || ticker?.ticker);
    if (!tickerSymbol) {
      return false;
    }

    return tickerSymbol === normalizeSymbol(quickTradeState.symbol);
  };

  // Helper function to safely get pre_close values
  const getPreCloseValue = useCallback((ticker) => {
    try {
      // First try account_ticker.pre_close which should be the most accurate value if available
      if (ticker.pre_close !== undefined && ticker.pre_close !== null) {
        const preCloseVal = typeof ticker.pre_close === 'string'
          ? parseFloat(ticker.pre_close) 
          : ticker.pre_close;
          
        if (!isNaN(preCloseVal)) {
          return preCloseVal;
        }
      }
      
      // If we don't have a valid pre_close but this ticker has current_price
      // Use previous_close from ticker if available (fallback for tickers endpoint)
      if (ticker.previous_close !== undefined && ticker.previous_close !== null) {
        const prevCloseVal = typeof ticker.previous_close === 'string'
          ? parseFloat(ticker.previous_close)
          : ticker.previous_close;
          
        if (!isNaN(prevCloseVal)) {
          return prevCloseVal;
        }
      }
      
      // As a last resort, use current_price if no other reference is available
      // This is not ideal but prevents 0% daily changes
      if (ticker.current_price !== undefined && ticker.current_price !== null) {
        return typeof ticker.current_price === 'string'
          ? parseFloat(ticker.current_price)
          : ticker.current_price;
      }
      
      // Return null for truly missing values
      return null;
    } catch (err) {
      logger.error('Error processing pre_close value:', err);
      return null;
    }
  }, []);

  const getEffectiveTodayGain = useCallback((ticker) => {
    const manualPrice = parseNumericValue(ticker.manual_price);
    const todayGainAmount = parseNumericValue(ticker.today_gain_amount) ?? 0;
    const todayGainPercentage = parseNumericValue(ticker.today_gain_percentage) ?? 0;

    if (!manualPrice || manualPrice <= 0) {
      return { amount: todayGainAmount, percentage: todayGainPercentage, isManual: false };
    }

    const preClose = getPreCloseValue(ticker);
    if (preClose === null || !Number.isFinite(preClose) || preClose === 0) {
      return { amount: todayGainAmount, percentage: todayGainPercentage, isManual: true };
    }

    const shares = parseNumericValue(ticker.shares) ?? 0;
    const amount = (manualPrice - preClose) * shares;
    const percentage = ((manualPrice - preClose) / preClose) * 100;

    return { amount, percentage, isManual: true };
  }, [parseNumericValue, getPreCloseValue]);

  const getStrategyInfo = useCallback((ticker) => {
    if (!ticker) {
      return null;
    }

    let strategyKey = null;
    const cacheKey = getStrategyCacheKey(ticker);

    // ALWAYS derive fresh strategy from ticker data first
    // This ensures that updated database values take precedence over stale cache
    const freshStrategyKey = deriveStrategyKeyFromTickerShields(ticker) || deriveStrategyKeyFromTickerFields(ticker);

    // DEBUG: Log strategy derivation details for troubleshooting
    if (ticker.symbol) {
      logger.debug(`[Strategy Debug] ${ticker.symbol}:`, {
        freshStrategyKey,
        cachedStrategyKey: strategyBadges[cacheKey],
        strategy_label: ticker.strategy_label,
        strategy: ticker.strategy,
        strategy_preset: ticker.strategy_preset,
        current_strategy: ticker.current_strategy,
        armor: ticker.armor,
        shields: ticker.shields?.map(s => ({ label: s.label, stop_loss_pct: s.stop_loss_pct }))
      });
    }

    // If we have fresh strategy data from the ticker, use it and update cache
    if (freshStrategyKey) {
      strategyKey = freshStrategyKey;
      // Update the cache with the fresh value
      if (cacheKey && strategyBadges[cacheKey] !== freshStrategyKey) {
        setStrategyBadges(prev => ({ ...prev, [cacheKey]: freshStrategyKey }));
      }
    } else if (cacheKey && Object.prototype.hasOwnProperty.call(strategyBadges, cacheKey)) {
      // Only fall back to cache if we couldn't derive from ticker data
      strategyKey = strategyBadges[cacheKey];
    }

    return getStrategyConfigForKey(strategyKey);
  }, [getStrategyCacheKey, strategyBadges]);

  // Helper function to render table cell based on column
  const renderTableCell = (ticker, column) => {
    const effectivePrice = ticker.effective_price || ticker.current_price;
    const isPriceUnavailable = effectivePrice === 0 || effectivePrice === null;
    
    const cellStyle = {
      ...getColumnStyle(column), 
      boxSizing: 'border-box',
      padding: '0.25rem', // Use consistent padding matching Transactions page py-1
      verticalAlign: 'middle',
      height: 'auto'  // Allow padding to control height consistently
    };
    
    // Create a helper function to determine if a ticker is in multiple accounts
    const isMultiAccount = (ticker) => {
      // Simply use the API-provided flag or check if it has multiple accounts
      return ticker.in_multiple_accounts === true || 
        (ticker.accounts && ticker.accounts.length > 1);
    };
    
    if (column !== 'symbol' && !visibleColumns[column]) {
      return null;
    }

    switch(column) {
      case 'symbol': {
        const manualIndicator = ticker.manual_price ? (
          <span
            className="text-info ms-1"
            title={`Manual price set on ${new Date(ticker.manual_price_set_at).toLocaleDateString()} - $${Number(ticker.manual_price).toFixed(2)}`}
          >
            📝
          </span>
        ) : null;

        const symbolLink = (
          <span
            style={{
              padding: '0.2rem 0.3rem',
              paddingLeft: '0',
              display: 'inline-flex',
              alignItems: 'center',
              cursor: 'pointer',
              color: '#0d6efd',
              fontWeight: '600'
            }}
            onClick={() => onTickerClick && onTickerClick(ticker.symbol, currentAccountId)}
            title="Click to view transaction history"
          >
            {ticker.symbol}
            {manualIndicator}
          </span>
        );

        const multiAccountContent = (
          <div className="d-flex align-items-center" style={{ width: '100%' }}>
            <div
              className="cursor-pointer"
              onClick={() => toggleExpandRow(ticker.ticker_id)}
              style={{ display: 'flex', alignItems: 'center', width: '100%', padding: '0' }}
            >
              <span
                className="multi-account-ticker"
                style={{ paddingLeft: '0', display: 'inline-flex', alignItems: 'center' }}
              >
                <Badge
                  bg="primary"
                  className="me-1 multi-account-badge"
                  style={{
                    fontSize: '0.7rem',
                    padding: '0.2em 0.5em',
                    fontWeight: '500',
                    letterSpacing: '0.02em',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
                  }}
                  title="This ticker exists in multiple accounts"
                >
                  Multi ({ticker.accounts ? ticker.accounts.length : '...'})
                </Badge>
                <span
                  style={{ fontWeight: '600', cursor: 'pointer', color: '#0d6efd', display: 'inline-flex', alignItems: 'center' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onTickerClick && onTickerClick(ticker.symbol);
                  }}
                  title="Click to view transaction history"
                >
                  {ticker.symbol}
                  {manualIndicator}
                </span>
              </span>
            </div>
          </div>
        );

        return (
          <td key={`${ticker.ticker_id}-${column}`} style={{ ...cellStyle, textAlign: 'left', paddingLeft: '0.4rem' }}>
            {isMultiAccount(ticker) ? (
              multiAccountContent
            ) : (
              <div className="d-flex align-items-center">
                {symbolLink}
              </div>
            )}
          </td>
        );
      }
      case 'strategy': {
        const strategyInfo = getStrategyInfo(ticker);

        // Get the strategy stop loss percentage
        const getStrategyStopLossPercent = () => {
          const strategyKey = strategyInfo?.key;
          if (!strategyKey) {
            return null;
          }

          // For T2-5-9, T3-7-12, and T5-10-15 all-in-one strategies, show the percentages
          if (strategyKey === 'T2-5-9') {
            return '-2%, -5%, -9%';
          }
          if (strategyKey === 'T3-7-12') {
            return '-3%, -7%, -12%';
          }
          if (strategyKey === 'T5-10-15') {
            return '-5%, -10%, -15%';
          }

          // For T-F hybrid strategies, show trailing + fixed stops
          const tfMatch = strategyKey.match(/^T(\d+)-F(\d+)$/);
          if (tfMatch) {
            return `T-${tfMatch[1]}% trim, F-${tfMatch[2]}% exit`;
          }

          // For entry strategies, the percentage is in the key (e.g., "entry-T7-F12" or legacy "entry-7-12")
          if (strategyKey.startsWith('entry-')) {
            const tokenPart = strategyKey.substring(6);
            const percentages = tokenPart.split('-').map(t => {
              const typed = t.match(/^[TF](\d+)$/);
              return typed ? typed[1] : (!isNaN(t) ? t : null);
            }).filter(Boolean);
            if (percentages.length > 0) {
              return percentages
                .map(p => (p === '0' ? '0%' : `-${p}%`))
                .join(', ');
            }
          }

          // For profit strategies
          if (strategyKey.startsWith('profit-')) {
            const percentPart = strategyKey.substring(7); // Remove "profit-" prefix
            if (percentPart && !isNaN(percentPart)) {
              return `+${percentPart}%`;
            }
          }

          // For named strategies (volatile, tight, strength, etc.), use predefined percentages
          const strategyConfig = STRATEGY_CONFIG_MAP[strategyKey];
          if (strategyConfig && strategyConfig.stopLossPercent !== null && strategyConfig.stopLossPercent !== undefined) {
            return `${strategyConfig.stopLossPercent}%`;
          }

          return null;
        };

        // Format tooltip to show strategy percentage
        const getStrategyTooltip = () => {
          const strategyKey = strategyInfo?.key;

          // For all-in strategies, show effective percentage instead of individual stops
          if (strategyKey && ['T2-5-9', 'T3-7-12', 'T5-10-15'].includes(strategyKey)) {
            const cacheKey = getStrategyCacheKey(ticker);
            const effectivePct = cacheKey ? effectivePercentageCacheRef.current.get(cacheKey) : null;

            if (effectivePct != null && typeof effectivePct === 'number') {
              return `${effectivePct.toFixed(2)}% effective`;
            }
          }

          const stopLossPercent = getStrategyStopLossPercent();
          if (stopLossPercent) {
            return stopLossPercent;
          }
          return strategyInfo ? `${strategyInfo.label} strategy` : '';
        };

        // Render badge label with emphasized number for all-in strategies
        const renderBadgeLabel = () => {
          const label = strategyInfo.label;
          const strategyKey = strategyInfo.key;

          // Check if this is an all-in strategy
          if (['T2-5-9', 'T3-7-12', 'T5-10-15'].includes(strategyKey)) {
            // Get the emphasized level from cache
            const cacheKey = getStrategyCacheKey(ticker);
            const emphasizedLevel = cacheKey ? emphasizedLevelCacheRef.current.get(cacheKey) : null;

            // Determine mode based on emphasized level position
            let modeIcon = null;
            if (emphasizedLevel) {
              // Map strategy key to stop levels
              const stopLevels = {
                'T2-5-9': [2, 5, 9],
                'T3-7-12': [3, 7, 12],
                'T5-10-15': [5, 10, 15]
              }[strategyKey];

              const emphasizedIndex = stopLevels?.indexOf(emphasizedLevel);
              if (emphasizedIndex === 0) {
                // 50% on first stop = defense mode
                modeIcon = <BsShieldFill style={{ marginLeft: '0.3em', fontSize: '0.85em' }} />;
              } else if (emphasizedIndex === 1) {
                // 50% on second stop = building mode
                modeIcon = <FaHammer style={{ marginLeft: '0.3em', fontSize: '0.85em' }} />;
              } else if (emphasizedIndex === 2) {
                // 50% on third stop = super building mode
                modeIcon = (
                  <>
                    <FaHammer style={{ marginLeft: '0.3em', fontSize: '0.85em' }} />
                    <FaHammer style={{ marginLeft: '0.1em', fontSize: '0.85em' }} />
                  </>
                );
              }
            }

            if (emphasizedLevel) {
              // Parse the label and emphasize the appropriate number
              // e.g., "T2-5-9" -> emphasize 2, 5, or 9 based on shield distribution
              const parts = label.split('-');
              return (
                <>
                  {parts.map((part, idx) => {
                    // Check if this part contains the emphasized number
                    const numMatch = part.match(/\d+/);
                    if (numMatch && parseInt(numMatch[0], 10) === emphasizedLevel) {
                      // This number should be emphasized
                      return (
                        <span key={idx}>
                          {part.substring(0, numMatch.index)}
                          <span style={{ fontSize: '1.15em', fontWeight: '700' }}>
                            {numMatch[0]}
                          </span>
                          {part.substring(numMatch.index + numMatch[0].length)}
                          {idx < parts.length - 1 ? '-' : ''}
                        </span>
                      );
                    }
                    return <span key={idx}>{part}{idx < parts.length - 1 ? '-' : ''}</span>;
                  })}
                  {modeIcon}
                </>
              );
            }
          }

          return label;
        };

        const badgeElement = strategyInfo ? (
          <Badge
            bg={strategyInfo.variant}
            className="ticker-strategy-badge"
            style={{
              fontSize: '0.78rem',
              padding: '0.2em 0.55em',
              fontWeight: '600',
              letterSpacing: '0.03em',
              textTransform: 'uppercase',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              color: strategyInfo.textColor || undefined
            }}
            title={getStrategyTooltip()}
          >
            {renderBadgeLabel()}
          </Badge>
        ) : (
          <span className="text-muted">—</span>
        );

        return (
          <td key={`${ticker.ticker_id}-${column}`} style={{ ...cellStyle, textAlign: 'center' }}>
            {badgeElement}
          </td>
        );
      }
      case 'stop': {
        // The shield array may already be inline on the ticker (aggregated
        // endpoints embed it directly) or, for the common per-account case,
        // sitting in the same shieldCacheRef the strategy badge above uses --
        // reuse it rather than firing a second fetch for the same data.
        const accountContextIdForStop = getAccountContextId(ticker);
        const rawShieldsForStop = Array.isArray(ticker.shields)
          ? ticker.shields
          : (Array.isArray(ticker.shields?.data)
            ? ticker.shields.data
            : shieldCacheRef.current[accountContextIdForStop]?.[ticker.ticker_id]);

        const activeStopShields = Array.isArray(rawShieldsForStop)
          ? rawShieldsForStop.filter(s => s && s.is_active !== false && s.shield_price !== undefined && s.shield_price !== null)
          : [];

        // With multiple shields (e.g. a T2-5-9 all-in strategy) the "current"
        // stop is the highest-priced active one -- it's the level closest to
        // the current price and therefore the next one a falling price hits.
        const currentStopShield = activeStopShields.reduce((best, shield) => {
          const price = parseFloat(shield.shield_price);
          if (isNaN(price)) return best;
          if (!best || price > parseFloat(best.shield_price)) return shield;
          return best;
        }, null);

        const stopPrice = currentStopShield ? parseFloat(currentStopShield.shield_price) : null;
        const purchasePrice = parseFloat(ticker.cost_basis);
        const isAboveCost = stopPrice !== null && !isNaN(purchasePrice) && stopPrice >= purchasePrice;

        return (
          <td key={`${ticker.ticker_id}-${column}`} style={{ ...cellStyle, textAlign: 'center' }}>
            {stopPrice !== null ? (
              <span
                className={isAboveCost ? 'text-success' : 'text-danger'}
                style={{ fontWeight: 'bold' }}
                title={currentStopShield.shield_name
                  ? `${currentStopShield.shield_name}: ${formatCurrency(stopPrice)}`
                  : formatCurrency(stopPrice)}
              >
                {formatCurrency(stopPrice)}
              </span>
            ) : (
              <span className="text-muted">—</span>
            )}
          </td>
        );
      }
      case 'notes':
        const notes = tickerNotesMap[ticker.ticker_id] || [];
        const hasNotes = notes.length > 0;
        const lastNote = hasNotes ? notes[0] : null;

        // Get the timestamp from ticker data if available, otherwise use lastNote
        const noteTimestamp = ticker.latest_note_updated_at || (lastNote && lastNote.updated_at);

        return (
          <td key={`${ticker.ticker_id}-${column}`} style={{ ...cellStyle, textAlign: 'center' }}>
            {hasNotes && noteTimestamp ? (() => {
              const noteDate = new Date(noteTimestamp);
              const today = new Date();
              const isToday = noteDate.toDateString() === today.toDateString();
              const dateStr = noteDate.toLocaleDateString('en-US', {
                month: 'numeric',
                day: 'numeric'
              }).replace(',', '');

              return (
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id={`tooltip-notes-${ticker.ticker_id}`}>
                      <div style={{ textAlign: 'left' }}>
                        {lastNote.note}
                      </div>
                    </Tooltip>
                  }
                >
                  <span style={{ cursor: 'pointer' }} onClick={() => handleNotesClick(ticker)}>
                    {isToday ? (
                      <span className="badge bg-success" style={{ fontSize: '0.7rem', fontWeight: 'normal' }}>
                        {dateStr}
                      </span>
                    ) : (
                      <span>{dateStr}</span>
                    )}
                  </span>
                </OverlayTrigger>
              );
            })() : (
              <span style={{ cursor: 'pointer' }} onClick={() => handleNotesClick(ticker)} className="text-muted">
                —
              </span>
            )}
          </td>
        );
      case 'actions':
        return (
          <td key={`${ticker.ticker_id}-${column}`} style={{...cellStyle, textAlign: 'center'}}>
            {/* Only show order and sell buttons for non-multi-account tickers */}
            {!isMultiAccount(ticker) && (
              <div className="d-flex justify-content-center gap-1">
                {!hideOrderButton && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className={`py-0 ${compactActions ? 'compact-action' : ''}`}
                    style={compactActions ? undefined : { fontSize: '0.75rem' }}
                    aria-label="Order"
                    title={onQuickBuy ? "Quick Buy (1/4 standard size)" : "Order"}
                    disabled={isQuickTradeActionDisabled(ticker, 'BUY')}
                    aria-busy={isQuickTradeActionDisabled(ticker, 'BUY') || undefined}
                    onClick={() => {
                      if (onQuickBuy && compactActions) {
                        onQuickBuy(ticker);
                      } else if (onShowOrderDialog) {
                        onShowOrderDialog(ticker);
                      }
                    }}
                  >
                    {compactActions ? '+' : 'Order'}
                  </Button>
                )}
                <Button
                  variant="outline-danger"
                  size="sm"
                  className={`py-0 ${compactActions ? 'compact-action' : ''}`}
                  style={compactActions ? undefined : { fontSize: '0.75rem' }}
                  aria-label="Sell"
                  title={onQuickSell ? "Quick Sell (1X shares)" : "Sell"}
                  disabled={isQuickTradeActionDisabled(ticker, 'SELL')}
                  aria-busy={isQuickTradeActionDisabled(ticker, 'SELL') || undefined}
                  onClick={() => {
                    if (onQuickSell && compactActions) {
                      onQuickSell(ticker);
                    } else if (onShowOrderDialog) {
                      onShowOrderDialog({ ...ticker, forceSell: true });
                    }
                  }}
                >
                  {compactActions ? '-' : 'Sell'}
                </Button>
              </div>
            )}
            {/* Add a toggle button for multi-account tickers */}
            {isMultiAccount(ticker) && (
              <Button
                variant="outline-primary"
                size="sm"
                className="py-0"
                style={{ fontSize: '0.75rem' }}
                onClick={() => toggleExpandRow(ticker.ticker_id)}
              >
                {expandedRows[ticker.ticker_id] ? 'Hide' : 'Details'}
              </Button>
            )}
          </td>
        );
      case 'name':
        return visibleColumns.name ? <td key={`${ticker.ticker_id}-${column}`} style={cellStyle}>{ticker.name}</td> : null;
      case 'rating':
        return visibleColumns.rating ? (
          <td key={`${ticker.ticker_id}-${column}`} style={{...cellStyle, textAlign: 'center'}}>
            <StarRating
              tickerId={ticker.ticker_id}
              symbol={ticker.symbol}
              currentRating={ticker.rating}
              onRatingChange={(savedRatingData) => {
                // Use the backend response as the authoritative saved value.
                const updatedTicker = savedRatingData && typeof savedRatingData === 'object'
                  ? { ...ticker, ...savedRatingData }
                  : { ...ticker, rating: savedRatingData };
                if (onTickerUpdate) {
                  // Check if onTickerUpdate expects an array or single ticker
                  // For rating updates, we'll pass the single updated ticker
                  onTickerUpdate(updatedTicker);
                }
              }}
              size="sm"
            />
          </td>
        ) : null;
      case 'entry': {
        if (!visibleColumns.entry) return null;
        const entryValue = normalizeEntryRatingValue(ticker.entry_rating);
        const entryStyle = ENTRY_RATING_STYLES[entryValue];
        return (
          <td
            key={`${ticker.ticker_id}-${column}`}
            style={{...cellStyle, textAlign: 'center', cursor: 'pointer'}}
            onClick={() => openEntryEditor(ticker)}
            title="Set entry rating"
          >
            {entryStyle ? (
              <Badge
                bg={entryStyle.variant}
                className={entryStyle.textClass}
                style={{
                  fontSize: '0.78rem',
                  padding: '0.2em 0.55em',
                  fontWeight: '600',
                  letterSpacing: '0.03em',
                  textTransform: 'uppercase',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                }}
              >
                {entryStyle.label}
              </Badge>
            ) : (
              <span className="text-muted">-</span>
            )}
          </td>
        );
      }
      case 'tsize':
        return visibleColumns.tsize ? (
          <td 
            key={`${ticker.ticker_id}-${column}`} 
            style={{...cellStyle, textAlign: 'right', cursor: 'pointer'}}
            onClick={() => handleTSizeClick(ticker)}
            title="Click to edit target size"
          >
            {ticker.tsize !== null && ticker.tsize !== undefined ? (
              <span style={{
                color: (() => {
                  // Display red if TSize is 0
                  if (ticker.tsize === 0) {
                    return '#dc3545';
                  }
                  const currentValue = ticker.current_value || (ticker.shares * ticker.cost_basis) || 0;
                  const positionSize = accountStandardSize && accountStandardSize > 0 ? currentValue / accountStandardSize : 0;
                  return positionSize >= ticker.tsize ? '#007bff' : 'inherit';
                })(),
                fontWeight: (() => {
                  const currentValue = ticker.current_value || (ticker.shares * ticker.cost_basis) || 0;
                  const positionSize = accountStandardSize && accountStandardSize > 0 ? currentValue / accountStandardSize : 0;
                  return positionSize >= ticker.tsize ? 'bold' : 'normal';
                })()
              }}>
                {formatNumber(ticker.tsize)}
              </span>
            ) : '-'}
          </td>
        ) : null;
      case 'next':
        if (!visibleColumns.next) return null;
        return (
          <td key={`${ticker.ticker_id}-${column}`} style={{ ...cellStyle, textAlign: 'right' }}>
            <Button
              size="sm"
              variant={(() => {
                const act = (ticker.next_action || 'HOLD');
                if (act === 'ADD') return 'success';
                if (act === 'TRIM' || act === 'EXIT') return 'danger';
                if (act === 'HOLD') return 'primary';
                return 'secondary';
              })()}
              className="py-0 fw-bold text-uppercase next-btn"
              onClick={(e) => { e.stopPropagation(); setEditingNextTicker(ticker); setNextEditorOpen(true); }}
              title="Set next plan"
            >
              {(() => {
                const act = (ticker.next_action || 'HOLD');
                const size = ticker.next_size_pct;
                const formatSize = (s) => {
                  if (s === 0.25) return '1/4x';
                  if (s === 0.5) return '1/2x';
                  if (s === 1) return '1x';
                  return Math.round(s * 100) + '%';
                };
                if (act === 'ADD') {
                  return `+${size ? formatSize(size) : ''}`;
                }
                if (act === 'TRIM') {
                  return `-${size ? formatSize(size) : ''}`;
                }
                return act;
              })()}
            </Button>
          </td>
        );
      case 'source':
        return visibleColumns.source ? (
          <td key={`${ticker.ticker_id}-${column}`} style={{...cellStyle, textAlign: 'right'}}>
            {!isMultiAccount(ticker) && ticker.account_name ? (
              <Link to={`/account/${ticker.account_id}`}>
                {ticker.account_name}
              </Link>
            ) : (
              <span className="text-muted">-</span>
            )}
          </td>
        ) : null;
      case 'category':
        return visibleColumns.category ? (
          <td key={`${ticker.ticker_id}-${column}`} style={{...cellStyle, textAlign: 'right'}}>
            <CategorySelect
              tickerId={ticker.ticker_id}
              currentCategory={ticker.category}
              onCategoryUpdate={handleCategoryUpdate}
              accountId={accountId}
            />
          </td>
        ) : null;
      case 'shares':
        return visibleColumns.shares ? <td key={`${ticker.ticker_id}-${column}`} style={{...cellStyle, textAlign: 'right'}}>{formatNumber(ticker.shares)}</td> : null;
      case 'cost_basis':
        return visibleColumns.cost_basis ? (
          <td key={`${ticker.ticker_id}-${column}`} style={{...cellStyle, textAlign: 'right'}}>
            {ticker.cost_basis ? (
              <span
                className={ticker.total_gain !== null && ticker.total_gain !== undefined ? `text-${getVariant(ticker.total_gain)}` : ''}
                style={{ fontWeight: 'bold' }}
              >
                {formatCurrency(ticker.cost_basis)}
              </span>
            ) : '-'}
          </td>
        ) : null;
      case 'total_cost':
        return visibleColumns.total_cost ? (
          <td key={`${ticker.ticker_id}-${column}`} style={{...cellStyle, textAlign: 'right'}}>
            {ticker.cost_basis ? (
              <span
                className={ticker.total_gain !== null && ticker.total_gain !== undefined ? `text-${getVariant(ticker.total_gain)}` : ''}
                style={{ fontWeight: 'bold' }}
              >
                {formatCurrency(ticker.cost_basis * ticker.shares)}
              </span>
            ) : '-'}
          </td>
        ) : null;
      case 'current_price':
        return visibleColumns.current_price ? (
          <td key={`${ticker.ticker_id}-${column}`} style={{...cellStyle, textAlign: 'right'}}>
            {isPriceUnavailable ? (
              <span className="text-warning">Unavailable</span>
            ) : (
              formatCurrency(ticker.effective_price || ticker.current_price)
            )}
          </td>
        ) : null;
      case 'pre_close':
        return visibleColumns.pre_close ? (
          <td key={`${ticker.ticker_id}-${column}`} style={{...cellStyle, textAlign: 'right'}}>
            {(() => {
              const preCloseValue = getPreCloseValue(ticker);
              return preCloseValue !== null ? formatCurrency(preCloseValue) : <span className="text-muted">-</span>;
            })()}
          </td>
        ) : null;
      case 'current_value':
        return visibleColumns.current_value ? (
          <td key={`${ticker.ticker_id}-${column}`} style={{...cellStyle, textAlign: 'right', fontWeight: 'bold', color: '#0d6efd'}}>
            {isPriceUnavailable ? (
              <>
                {ticker.cost_basis ? (
                  <div className="position-relative">
                    {formatCurrencyWhole(ticker.cost_basis * ticker.shares)}
                    <span className="text-muted small ms-1" title="Calculated from cost basis">(est.)</span>
                  </div>
                ) : (
                  <span className="text-warning">Unknown</span>
                )}
              </>
            ) : (
              formatCurrencyWhole(ticker.current_value)
            )}
          </td>
        ) : null;
      case 'high':
        return visibleColumns.high ? (
          <td key={`${ticker.ticker_id}-${column}`} style={{...cellStyle, textAlign: 'center'}}>
            {renderHighStatusIcon(
              ticker.is_new_high_today,
              `ticker-${ticker.ticker_id}`
            )}
          </td>
        ) : null;
      case 'today_gain_amount':
        return visibleColumns.today_gain_amount ? (
          <td key={`${ticker.ticker_id}-${column}`} style={{...cellStyle, textAlign: 'right'}}>
            {isPriceUnavailable ? (
              <span className="text-muted">N/A</span>
            ) : ticker.is_new_ticker ? (
              <span className="text-muted" title="Waiting for reliable price data...">
                <i className="bi bi-hourglass-split me-1"></i>
                Loading...
              </span>
            ) : (
              (() => {
                const { amount } = getEffectiveTodayGain(ticker);
                return (
                  <span 
                    className={`text-${getVariant(amount)}`}
                    style={{ fontWeight: 'bold' }}
                  >
                    {amount >= 0 ? '' : '-'}{formatCurrency(Math.abs(amount))}
                  </span>
                );
              })()
            )}
          </td>
        ) : null;
      case 'today_gain_percentage':
        return visibleColumns.today_gain_percentage ? (
          <td key={`${ticker.ticker_id}-${column}`} style={{...cellStyle, textAlign: 'right'}}>
            {isPriceUnavailable ? (
              <span className="text-muted">N/A</span>
            ) : ticker.is_new_ticker ? (
              <span className="text-muted" title="Waiting for reliable price data...">
                <i className="bi bi-hourglass-split me-1"></i>
                Loading...
              </span>
            ) : (
              (() => {
                const { amount, percentage } = getEffectiveTodayGain(ticker);
                return (
                  <OverlayTrigger
                    placement="top"
                    delay={{ show: 200, hide: 100 }}
                    overlay={
                      <Tooltip id={`today-gain-pct-tooltip-${ticker.ticker_id}`}>
                        <span style={{ color: amount >= 0 ? '#28d17c' : '#ff3b3b', fontWeight: 700, fontSize: '1.35rem', lineHeight: 1 }}>
                          {amount >= 0 ? '' : '-'}{formatCurrency(Math.abs(amount))}
                        </span>
                      </Tooltip>
                    }
                    trigger={["hover", "focus", "click"]}
                  >
                    <span 
                      className={`text-${getVariant(percentage)}`}
                      style={{ fontWeight: 'bold', cursor: 'pointer' }}
                    >
                      {percentage >= 0 ? '' : '-'}{formatPercentage(Math.abs(percentage))}
                    </span>
                  </OverlayTrigger>
                );
              })()
            )}
          </td>
        ) : null;
      case 'total_gain':
        return visibleColumns.total_gain ? (
          <td key={`${ticker.ticker_id}-${column}`} style={{...cellStyle, textAlign: 'right'}}>
            {isPriceUnavailable ? (
              <span className="text-muted">N/A</span>
            ) : ticker.is_new_ticker ? (
              <span className="text-muted" title="Waiting for reliable price data...">
                <i className="bi bi-hourglass-split me-1"></i>
                Loading...
              </span>
            ) : (
              <span 
                className={`text-${getVariant(ticker.total_gain)}`}
                style={{ fontWeight: 'bold' }}
              >
                {ticker.total_gain >= 0 ? '' : '-'}{formatCurrency(Math.abs(ticker.total_gain))}
              </span>
            )}
          </td>
        ) : null;
      case 'unrealized_gain':
        return visibleColumns.unrealized_gain ? (
          <td key={`${ticker.ticker_id}-${column}`} style={{...cellStyle, textAlign: 'right'}}>
            {isPriceUnavailable ? (
              <span className="text-muted">N/A</span>
            ) : ticker.is_new_ticker ? (
              <span className="text-muted" title="Waiting for reliable price data...">
                <i className="bi bi-hourglass-split me-1"></i>
                Loading...
              </span>
            ) : ticker.unrealized_gain == null ? (
              <span className="text-muted">-</span>
            ) : (
              <span
                className={`text-${getVariant(ticker.unrealized_gain)}`}
                style={{ fontWeight: 'bold' }}
              >
                {ticker.unrealized_gain >= 0 ? '' : '-'}{formatCurrencyWhole(Math.abs(ticker.unrealized_gain))}
              </span>
            )}
          </td>
        ) : null;
      case 'total_gain_pct':
        return visibleColumns.total_gain_pct ? (
          <td key={`${ticker.ticker_id}-${column}`} style={{...cellStyle, textAlign: 'right'}}>
            {isPriceUnavailable ? (
              <span className="text-muted">N/A</span>
            ) : ticker.is_new_ticker ? (
              <span className="text-muted" title="Waiting for reliable price data...">
                <i className="bi bi-hourglass-split me-1"></i>
                Loading...
              </span>
            ) : (
              <OverlayTrigger
                placement="top"
                delay={{ show: 200, hide: 100 }}
                overlay={
                  <Tooltip id={`total-gain-pct-tooltip-${ticker.ticker_id}`}>
                    <span style={{ color: ticker.total_gain >= 0 ? '#28d17c' : '#ff3b3b', fontWeight: 700, fontSize: '1.35rem', lineHeight: 1 }}>
                      {ticker.total_gain >= 0 ? '' : '-'}{formatCurrency(Math.abs(ticker.total_gain))}
                    </span>
                  </Tooltip>
                }
                trigger={["hover", "focus", "click"]}
              >
                <span 
                  className={`text-${getVariant(ticker.total_gain_pct)}`}
                  style={{ fontWeight: 'bold', cursor: 'pointer' }}
                >
                  {ticker.total_gain_pct >= 0 ? '' : '-'}{formatPercentage(Math.abs(ticker.total_gain_pct))}
                </span>
              </OverlayTrigger>
            )}
          </td>
        ) : null;
      case 'trade_gain':
        return visibleColumns.trade_gain ? (
          <td key={`${ticker.ticker_id}-${column}`} style={{...cellStyle, textAlign: 'right'}}>
            {(() => {
              const { tradeId, gainPct } = getCurrentTradeInfo(ticker);
              const tradeGainAmount = getTradeGainAmount(ticker, gainPct);
              if (tradeGainAmount === null || tradeGainAmount === undefined) {
                return <span className="text-muted">-</span>;
              }
              const tooltipContent = gainPct !== null && gainPct !== undefined
                ? (
                  <span style={{ color: gainPct >= 0 ? '#28d17c' : '#ff3b3b', fontWeight: 700, fontSize: '1.1rem', lineHeight: 1 }}>
                    {gainPct >= 0 ? '' : '-'}{formatPercentage(Math.abs(gainPct))}
                  </span>
                )
                : <span className="text-muted">Percentage unavailable</span>;
              const content = (
                <span
                  className={`text-${getVariant(tradeGainAmount)}`}
                  style={{ fontWeight: 'bold', cursor: tradeId ? 'pointer' : 'default' }}
                >
                  {tradeGainAmount >= 0 ? '' : '-'}{formatCurrencyWhole(Math.abs(tradeGainAmount))}
                </span>
              );
              const wrappedContent = tradeId ? (
                <Link to={`/trades/${tradeId}`} style={{ textDecoration: 'none' }}>
                  {content}
                </Link>
              ) : content;
              return (
                <OverlayTrigger
                  placement="top"
                  delay={{ show: 200, hide: 100 }}
                  overlay={
                    <Tooltip id={`trade-gain-tooltip-${ticker.ticker_id}`}>
                      {tooltipContent}
                    </Tooltip>
                  }
                  trigger={["hover", "focus", "click"]}
                >
                  <span style={{ display: 'inline-block' }}>
                    {wrappedContent}
                  </span>
                </OverlayTrigger>
              );
            })()}
          </td>
        ) : null;
      case 'trade_gain_pct':
        return visibleColumns.trade_gain_pct ? (
          <td key={`${ticker.ticker_id}-${column}`} style={{...cellStyle, textAlign: 'right'}}>
            {(() => {
              const { tradeId, gainPct } = getCurrentTradeInfo(ticker);
              const tradeGainAmount = getTradeGainAmount(ticker, gainPct);
              if (gainPct === null || gainPct === undefined) {
                return <span className="text-muted">-</span>;
              }
              const tooltipContent = tradeGainAmount !== null
                ? (
                  <span style={{ color: tradeGainAmount >= 0 ? '#28d17c' : '#ff3b3b', fontWeight: 700, fontSize: '1.1rem', lineHeight: 1 }}>
                    {tradeGainAmount >= 0 ? '' : '-'}{formatCurrency(Math.abs(tradeGainAmount))}
                  </span>
                )
                : <span className="text-muted">Gain data unavailable</span>;
              const content = (
                <span
                  className={`text-${getVariant(gainPct)}`}
                  style={{ fontWeight: 'bold', cursor: tradeId ? 'pointer' : 'default' }}
                >
                  {gainPct >= 0 ? '' : '-'}{formatPercentage(Math.abs(gainPct))}
                </span>
              );
              const wrappedContent = tradeId ? (
                <Link to={`/trades/${tradeId}`} style={{ textDecoration: 'none' }}>
                  {content}
                </Link>
              ) : content;
              return (
                <OverlayTrigger
                  placement="top"
                  delay={{ show: 200, hide: 100 }}
                  overlay={
                    <Tooltip id={`trade-gain-pct-tooltip-${ticker.ticker_id}`}>
                      {tooltipContent}
                    </Tooltip>
                  }
                  trigger={["hover", "focus", "click"]}
                >
                  <span style={{ display: 'inline-block' }}>
                    {wrappedContent}
                  </span>
                </OverlayTrigger>
              );
            })()}
          </td>
        ) : null;
      case 'days_held':
        return visibleColumns.days_held ? (
          <td key={`${ticker.ticker_id}-${column}`} style={{...cellStyle, textAlign: 'right'}}>
            <div className="text-end">
              <span 
                style={{ fontWeight: 'bold', color: '#0dcaf0' }}
                title={ticker.in_multiple_accounts ? "Showing days held since earliest purchase across all accounts" : undefined}
              >
                {ticker.days_held !== undefined ? ticker.days_held : '-'}
                {ticker.in_multiple_accounts && <i className="bi bi-calendar-check ms-1" style={{ fontSize: '0.7rem' }}></i>}
              </span>
            </div>
          </td>
        ) : null;
      case 'position_size':
        return visibleColumns.position_size ? (
          <td key={`${ticker.ticker_id}-${column}`} style={{...cellStyle, textAlign: 'right'}}>
            <div className="text-end">
              {calculatePositionSize(ticker.current_value) !== null ? (
                <span
                  style={{ fontWeight: 'bold', color: '#0d6efd' }}
                  title={calculatePositionSize(ticker.current_value).toFixed(2)}
                >
                  {formatPositionSizeFraction(calculatePositionSize(ticker.current_value))}
                </span>
              ) : (
                '-'
              )}
            </div>
          </td>
        ) : null;
      case 'armor':
        return visibleColumns.armor ? (
          <td key={`${ticker.ticker_id}-${column}`} style={cellStyle}>
            <div className="text-center">
              {isMultiAccount(ticker) ? (
                <span className="text-muted" style={{ fontStyle: 'italic', fontSize: '0.9rem' }}>
                  {/* Leave empty for multi-account tickers */}
                </span>
              ) : (
                <ArmorEdit
                  accountId={accountId || ticker.account_id}
                  tickerId={ticker.ticker_id}
                  currentArmor={ticker.armor}
                  onArmorUpdate={handleArmorUpdate}
                  onShieldUpdate={handleShieldRefresh}
                  tradeId={getCurrentTradeInfo(ticker).tradeId}
                />
              )}
            </div>
          </td>
        ) : null;
      case 'risk_gain':
        return visibleColumns.risk_gain ? (
          <td key={`${ticker.ticker_id}-${column}`} style={{...cellStyle, textAlign: 'right'}}>
            <div className="text-end">
              {(() => {
                // For multi-account tickers, calculate and show aggregated risk
                const riskGain = isMultiAccount(ticker)
                  ? getAggregatedRiskGain(ticker)
                  : getRiskGain(ticker, null);

                if (riskGain.value === null) return <span className="text-muted">-</span>;

                return (
                  <span
                    className={`text-${riskGain.isProfit ? 'success' : 'danger'}`}
                    style={{ fontWeight: 'bold' }}
                  >
                    {formatPercentage(riskGain.value)}
                  </span>
                );
              })()}
            </div>
          </td>
        ) : null;
      case 'trisk_gain':
        return visibleColumns.trisk_gain ? (
          <td key={`${ticker.ticker_id}-${column}`} style={{...cellStyle, textAlign: 'right'}}>
            <div className="text-end">
              {(() => {
                // For multi-account tickers, calculate and show aggregated risk
                const riskGain = isMultiAccount(ticker)
                  ? getAggregatedRiskGain(ticker)
                  : getRiskGain(ticker, null);

                // Calculate TRisk(%) as risk amount divided by total account value
                if (riskGain.amount === null || !accountTotalValue || accountTotalValue <= 0) {
                  return <span className="text-muted">-</span>;
                }

                const triskPercentage = (riskGain.amount / accountTotalValue) * 100;

                return (
                  <span
                    className={
                      triskPercentage > 0
                        ? 'text-success'
                        : (triskPercentage >= -2.5 ? 'text-primary' : 'text-danger')
                    }
                    style={{
                      fontWeight: 'bold',
                      fontSize: triskPercentage <= -2.5 ? '1.1em' : 'inherit'
                    }}
                  >
                    {formatPercentage(triskPercentage)}
                  </span>
                );
              })()}
            </div>
          </td>
        ) : null;
      case 'risk_gain_amount':
        return visibleColumns.risk_gain_amount ? (
          <td key={`${ticker.ticker_id}-${column}`} style={{...cellStyle, textAlign: 'right'}}>
            <div className="text-end">
              {(() => {
                // For multi-account tickers, calculate and show aggregated risk
                const riskGain = isMultiAccount(ticker)
                  ? getAggregatedRiskGain(ticker)
                  : getRiskGain(ticker, null);

                if (riskGain.amount === null) return <span className="text-muted">-</span>;

                return (
                  <span
                    className={`text-${riskGain.isProfit ? 'success' : 'danger'}`}
                    style={{ fontWeight: 'bold' }}
                  >
                    {formatRiskAmount(riskGain.amount)}
                  </span>
                );
              })()}
            </div>
          </td>
        ) : null;
      case 'theme': {
        if (!visibleColumns.theme) {
          return null;
        }

        const isMulti = isMultiAccount(ticker);
        const isExpanded = Boolean(expandedRows[ticker.ticker_id]);
        const aggregatedThemeEnabled = isMulti ? getAggregatedThemeState(ticker) : ticker.theme_enabled;

        return (
          <td key={`${ticker.ticker_id}-${column}`} style={cellStyle}>
            <div className="text-center">
              {isMulti ? (
                isExpanded ? (
                  <span className="text-muted" style={{ fontStyle: 'italic', fontSize: '0.9rem' }}>
                    {/* Hide combined star while expanded to avoid duplicate controls */}
                  </span>
                ) : (
                  <button
                    type="button"
                    className="btn btn-link p-0"
                    onClick={() => handleThemeToggle(ticker)}
                    aria-label={`Toggle theme ${aggregatedThemeEnabled ? 'enabled' : 'disabled'} for all accounts`}
                    title={`Click to ${aggregatedThemeEnabled ? 'disable' : 'enable'} theme for all accounts`}
                    style={{ textDecoration: 'none' }}
                  >
                    {renderThemeIcon(aggregatedThemeEnabled, THEME_ICON_MAIN_SIZE)}
                  </button>
                )
              ) : (
                <button
                  type="button"
                  className="btn btn-link p-0"
                  onClick={() => handleThemeToggle({ ...ticker, account_id: accountId || ticker.account_id })}
                  aria-label={`Toggle theme ${ticker.theme_enabled ? 'enabled' : 'disabled'}`}
                  title={ticker.theme_enabled ? 'Theme enabled' : 'Theme disabled'}
                  style={{ textDecoration: 'none' }}
                >
                  {renderThemeIcon(ticker.theme_enabled, THEME_ICON_MAIN_SIZE)}
                </button>
              )}
            </div>
          </td>
        );
      }
      case 'base': {
        if (!visibleColumns.base) {
          return null;
        }

        const isMulti = isMultiAccount(ticker);
        const isExpanded = Boolean(expandedRows[ticker.ticker_id]);
        const aggregatedBaseEnabled = isMulti ? getAggregatedBaseState(ticker) : ticker.base_enabled;

        return (
          <td key={`${ticker.ticker_id}-${column}`} style={cellStyle}>
            <div className="text-center">
              {isMulti ? (
                isExpanded ? (
                  <span className="text-muted" style={{ fontStyle: 'italic', fontSize: '0.9rem' }}>
                    {/* Hide combined star while expanded to avoid duplicate controls */}
                  </span>
                ) : (
                  <button
                    type="button"
                    className="btn btn-link p-0"
                    onClick={() => handleBaseToggle(ticker)}
                    aria-label={`Toggle base ${aggregatedBaseEnabled ? 'enabled' : 'disabled'} for all accounts`}
                    title={`Click to ${aggregatedBaseEnabled ? 'disable' : 'enable'} base for all accounts`}
                    style={{ textDecoration: 'none' }}
                  >
                    {renderThemeIcon(aggregatedBaseEnabled, THEME_ICON_MAIN_SIZE)}
                  </button>
                )
              ) : (
                <button
                  type="button"
                  className="btn btn-link p-0"
                  onClick={() => handleBaseToggle({ ...ticker, account_id: accountId || ticker.account_id })}
                  aria-label={`Toggle base ${ticker.base_enabled ? 'enabled' : 'disabled'}`}
                  title={ticker.base_enabled ? 'Base enabled' : 'Base disabled'}
                  style={{ textDecoration: 'none' }}
                >
                  {renderThemeIcon(ticker.base_enabled, THEME_ICON_MAIN_SIZE)}
                </button>
              )}
            </div>
          </td>
        );
      }
      default:
        return null;
    }
  };

  // Identity of which tickers are currently present, independent of their
  // field values or fetch/poll order. Row ORDER below is only recomputed
  // when this set changes (or the user changes what/how we sort) -- not
  // every time a background price/metric update (e.g. the live poll merge
  // in AccountDetails' subscribeToTickerUpdates handler) merely changes
  // values within the same set of tickers. Without this, prices landing
  // moments after the table first paints -- which now happens quickly and
  // reliably -- would immediately re-sort the table out from under the
  // user, showing one order and then "fixing" it to another.
  const tickerIdsKey = useMemo(() => {
    if (!tickers || tickers.length === 0) {
      return '';
    }
    return tickers.map(t => t.ticker_id).sort().join(',');
  }, [tickers]);

  // Live lookup so sortedTickers below can carry current field values
  // (price, gain, etc.) even while the order stays frozen between resorts.
  const tickersById = useMemo(() => {
    const map = new Map();
    (tickers || []).forEach(t => map.set(t.ticker_id, t));
    return map;
  }, [tickers]);

  // Sort order: recomputed only when the ticker set or sort criteria change
  // (see tickerIdsKey above), not on every value-only tickers update.
  const sortedTickerIds = useMemo(() => {
    if (!tickers || tickers.length === 0) {
      return [];
    }

    // Precompute strategy info once per ticker instead of calling
    // getStrategyInfo(a)/getStrategyInfo(b) inside the sort comparator
    // below: Array.sort() invokes the comparator O(n log n) times, which
    // was multiplying getStrategyInfo's non-trivial derivation (plus a
    // debug log call, plus a possible strategyBadges cache update) by
    // log n for no reason -- O(n) work here instead.
    let strategyInfoByTickerId = null;
    if (sortColumn === 'strategy') {
      strategyInfoByTickerId = new Map();
      tickers.forEach(t => strategyInfoByTickerId.set(t.ticker_id, getStrategyInfo(t)));
    }

    return [...tickers].sort((a, b) => {
        let aValue, bValue;

        // Special case for symbol sorting - group multi-account tickers together
        if (sortColumn === 'symbol' && multiSortMode === 'grouped') {
          // First, check multi-account status
          const aIsMulti = a.in_multiple_accounts === true;
          const bIsMulti = b.in_multiple_accounts === true;
          
          // If we're sorting by multi status first
          if (aIsMulti !== bIsMulti) {
            // If ascending, multi accounts come first
            // If descending, non-multi accounts come first
            return sortDirection === 'asc' 
              ? (aIsMulti ? -1 : 1) 
              : (aIsMulti ? 1 : -1);
          }
          
          // If both are multi or both are not multi, sort by symbol
          aValue = a.symbol;
          bValue = b.symbol;
        } else {
          // When sorting by current_price or value-related columns, use cost_basis for calculation if current_price is missing
          const aHasPrice = Boolean(a.current_price);
          const bHasPrice = Boolean(b.current_price);

          switch (sortColumn) {
            case 'strategy': {
              const infoA = strategyInfoByTickerId.get(a.ticker_id);
              const infoB = strategyInfoByTickerId.get(b.ticker_id);

              const getStrategyRank = (info) => {
                if (!info || !info.key) {
                  return STRATEGY_PRIORITY.length;
                }
                const idx = STRATEGY_PRIORITY.indexOf(info.key);
                return idx === -1 ? STRATEGY_PRIORITY.length : idx;
              };

              const rankA = getStrategyRank(infoA);
              const rankB = getStrategyRank(infoB);

              if (rankA !== rankB) {
                return sortDirection === 'asc' ? rankA - rankB : rankB - rankA;
              }

              const labelComparison = (infoA?.label || '').localeCompare(infoB?.label || '');
              if (labelComparison !== 0) {
                return sortDirection === 'asc' ? labelComparison : -labelComparison;
              }

              // For all-in strategies with the same label, sort by effective percentage
              if (infoA?.key && ['T2-5-9', 'T3-7-12', 'T5-10-15'].includes(infoA.key)) {
                const cacheKeyA = getStrategyCacheKey(a);
                const cacheKeyB = getStrategyCacheKey(b);
                const effectivePctA = cacheKeyA ? effectivePercentageCacheRef.current.get(cacheKeyA) : null;
                const effectivePctB = cacheKeyB ? effectivePercentageCacheRef.current.get(cacheKeyB) : null;

                if (effectivePctA !== null && effectivePctB !== null) {
                  const pctComparison = effectivePctA - effectivePctB;
                  if (pctComparison !== 0) {
                    return sortDirection === 'asc' ? pctComparison : -pctComparison;
                  }
                }
              }

              aValue = a.symbol || '';
              bValue = b.symbol || '';
              break;
            }
            case 'name':
              aValue = a.name;
              bValue = b.name;
              break;
            case 'category':
              aValue = a.category?.name || '';
              bValue = b.category?.name || '';

              // Keep intra-category ordering deterministic: symbol A -> Z
              if (aValue === bValue) {
                const symbolComparison = (a.symbol || '').localeCompare(b.symbol || '');
                if (symbolComparison !== 0) {
                  return symbolComparison;
                }
              }
              break;
            case 'shares':
              aValue = a.shares;
              bValue = b.shares;
              break;
            case 'cost_basis':
              aValue = a.cost_basis || 0;
              bValue = b.cost_basis || 0;
              break;
            case 'current_price':
              aValue = aHasPrice ? a.current_price : (a.cost_basis || 0);
              bValue = bHasPrice ? b.current_price : (b.cost_basis || 0);
              break;
            case 'current_value':
              aValue = aHasPrice ? a.current_value : (a.cost_basis ? (a.shares * a.cost_basis) : 0);
              bValue = bHasPrice ? b.current_value : (b.cost_basis ? (b.shares * b.cost_basis) : 0);
              break;
            case 'today_gain_amount':
              // If price is unavailable, put these items at the bottom
              if (!aHasPrice && !bHasPrice) {
                aValue = 0;
                bValue = 0;
              } else if (!aHasPrice) {
                return 1; // a goes to the bottom
              } else if (!bHasPrice) {
                return -1; // b goes to the bottom
              } else {
                aValue = getEffectiveTodayGain(a).amount;
                bValue = getEffectiveTodayGain(b).amount;
              }
              break;
            case 'today_gain_percentage':
              // If price is unavailable, put these items at the bottom
              if (!aHasPrice && !bHasPrice) {
                aValue = 0;
                bValue = 0;
              } else if (!aHasPrice) {
                return 1; // a goes to the bottom
              } else if (!bHasPrice) {
                return -1; // b goes to the bottom
              } else {
                aValue = getEffectiveTodayGain(a).percentage;
                bValue = getEffectiveTodayGain(b).percentage;
              }
              break;
            case 'total_gain':
              // If price is unavailable, put these items at the bottom
              if (!aHasPrice && !bHasPrice) {
                aValue = 0;
                bValue = 0;
              } else if (!aHasPrice) {
                return 1; // a goes to the bottom
              } else if (!bHasPrice) {
                return -1; // b goes to the bottom
              } else {
                aValue = a.total_gain;
                bValue = b.total_gain;
              }
              break;
            case 'total_gain_pct':
              // If price is unavailable, put these items at the bottom
              if (!aHasPrice && !bHasPrice) {
                aValue = 0;
                bValue = 0;
              } else if (!aHasPrice) {
                return 1; // a goes to the bottom
              } else if (!bHasPrice) {
                return -1; // b goes to the bottom
              } else {
                aValue = a.total_gain_pct;
                bValue = b.total_gain_pct;
              }
              break;
            case 'unrealized_gain':
              if (!aHasPrice && !bHasPrice) {
                aValue = 0;
                bValue = 0;
              } else if (!aHasPrice) {
                return 1;
              } else if (!bHasPrice) {
                return -1;
              } else {
                aValue = a.unrealized_gain ?? 0;
                bValue = b.unrealized_gain ?? 0;
              }
              break;
            case 'trade_gain_pct': {
              const aTrade = getCurrentTradeInfo(a);
              const bTrade = getCurrentTradeInfo(b);
              const aHasGain = aTrade.gainPct !== null && aTrade.gainPct !== undefined;
              const bHasGain = bTrade.gainPct !== null && bTrade.gainPct !== undefined;

              if (!aHasGain && !bHasGain) {
                aValue = 0;
                bValue = 0;
              } else if (!aHasGain) {
                return 1;
              } else if (!bHasGain) {
                return -1;
              } else {
                aValue = aTrade.gainPct;
                bValue = bTrade.gainPct;
              }
              break;
            }
            case 'trade_gain': {
              const aTradeInfo = getCurrentTradeInfo(a);
              const bTradeInfo = getCurrentTradeInfo(b);
              const aGainAmount = getTradeGainAmount(a, aTradeInfo.gainPct);
              const bGainAmount = getTradeGainAmount(b, bTradeInfo.gainPct);
              const aHasGainDollar = aGainAmount !== null && aGainAmount !== undefined;
              const bHasGainDollar = bGainAmount !== null && bGainAmount !== undefined;

              if (!aHasGainDollar && !bHasGainDollar) {
                aValue = 0;
                bValue = 0;
              } else if (!aHasGainDollar) {
                return 1;
              } else if (!bHasGainDollar) {
                return -1;
              } else {
                aValue = aGainAmount;
                bValue = bGainAmount;
              }
              break;
            }
            case 'days_held':
              // Only use days_held from the API response
              aValue = a.days_held !== undefined ? a.days_held : 0;
              bValue = b.days_held !== undefined ? b.days_held : 0;
              break;
            case 'position_size':
              aValue = calculatePositionSize(a.current_value) || 0;
              bValue = calculatePositionSize(b.current_value) || 0;
              break;
            case 'armor':
              aValue = a.armor || 0;
              bValue = b.armor || 0;
              break;
            case 'rating':
              aValue = a.rating || 0;
              bValue = b.rating || 0;
              
              // If ratings are the same, sort by current value (highest first)
              if (aValue === bValue) {
                const aCurrentValue = a.current_value || (a.shares * a.cost_basis) || 0;
                const bCurrentValue = b.current_value || (b.shares * b.cost_basis) || 0;
                return bCurrentValue - aCurrentValue; // Always highest value first
              }
              break;
            case 'entry':
              aValue = (a.entry_rating || '').toUpperCase();
              bValue = (b.entry_rating || '').toUpperCase();
              break;
            case 'tsize':
              aValue = a.tsize !== null && a.tsize !== undefined ? a.tsize : 0;
              bValue = b.tsize !== null && b.tsize !== undefined ? b.tsize : 0;
              break;
            case 'risk_gain': {
              // Calculate risk percentage for sorting, using aggregated values for multi-account tickers
              const aRiskGainResult = (a.in_multiple_accounts || (a.accounts && a.accounts.length > 1))
                ? getAggregatedRiskGain(a)
                : getRiskGain(a);
              const bRiskGainResult = (b.in_multiple_accounts || (b.accounts && b.accounts.length > 1))
                ? getAggregatedRiskGain(b)
                : getRiskGain(b);
              // Tickers with no stop set (value === null) always go to the
              // bottom, regardless of sort direction -- matching the
              // direction-independent return-1/-1 convention used by
              // today_gain_amount/total_gain/etc. above. The previous
              // -999999 sentinel fed into the direction-aware aValue-bValue
              // comparison below, so ascending sort (numerically smallest
              // first) put no-stop tickers at the TOP instead.
              if (aRiskGainResult.value === null && bRiskGainResult.value === null) {
                aValue = 0;
                bValue = 0;
              } else if (aRiskGainResult.value === null) {
                return 1; // a goes to the bottom
              } else if (bRiskGainResult.value === null) {
                return -1; // b goes to the bottom
              } else {
                aValue = aRiskGainResult.value;
                bValue = bRiskGainResult.value;
              }
              break;
            }
            case 'risk_gain_amount': {
              // Calculate risk dollar amount for sorting, using aggregated values for multi-account tickers
              const aRiskGainAmountResult = (a.in_multiple_accounts || (a.accounts && a.accounts.length > 1))
                ? getAggregatedRiskGain(a)
                : getRiskGain(a);
              const bRiskGainAmountResult = (b.in_multiple_accounts || (b.accounts && b.accounts.length > 1))
                ? getAggregatedRiskGain(b)
                : getRiskGain(b);
              // Same no-stop-always-goes-to-the-bottom fix as risk_gain above.
              if (aRiskGainAmountResult.amount === null && bRiskGainAmountResult.amount === null) {
                aValue = 0;
                bValue = 0;
              } else if (aRiskGainAmountResult.amount === null) {
                return 1; // a goes to the bottom
              } else if (bRiskGainAmountResult.amount === null) {
                return -1; // b goes to the bottom
              } else {
                aValue = aRiskGainAmountResult.amount;
                bValue = bRiskGainAmountResult.amount;
              }
              break;
            }
            case 'theme': {
              const aThemeEnabled = getAggregatedThemeState(a);
              const bThemeEnabled = getAggregatedThemeState(b);

              // Sort enabled stars ahead of disabled ones when descending
              aValue = aThemeEnabled ? 1 : 0;
              bValue = bThemeEnabled ? 1 : 0;

              // If both have same state, fall back to symbol ordering for stability
              if (aValue === bValue) {
                const comparison = (a.symbol || '').localeCompare(b.symbol || '');
                if (comparison !== 0) {
                  return sortDirection === 'asc' ? comparison : -comparison;
                }
              }
              break;
            }
            case 'base': {
              const aBaseEnabled = getAggregatedBaseState(a);
              const bBaseEnabled = getAggregatedBaseState(b);

              aValue = aBaseEnabled ? 1 : 0;
              bValue = bBaseEnabled ? 1 : 0;

              if (aValue === bValue) {
                const comparison = (a.symbol || '').localeCompare(b.symbol || '');
                if (comparison !== 0) {
                  return sortDirection === 'asc' ? comparison : -comparison;
                }
              }
              break;
            }
            case 'next': {
              // Map actions to ranks for sorting; higher rank = more actionable
              const rankMap = { 'HOLD': 0, 'EXIT': 1, 'TRIM': 2, 'ADD': 3 };
              const aAct = (a.next_action || 'HOLD').toUpperCase();
              const bAct = (b.next_action || 'HOLD').toUpperCase();
              const aRank = rankMap[aAct] ?? 0;
              const bRank = rankMap[bAct] ?? 0;
              const aSize = typeof a.next_size_pct === 'number' ? a.next_size_pct : 0;
              const bSize = typeof b.next_size_pct === 'number' ? b.next_size_pct : 0;
              // Combine rank and size so items of same action sort by size
              aValue = aRank * 1000 + Math.round(aSize * 100);
              bValue = bRank * 1000 + Math.round(bSize * 100);
              break;
            }
            case 'notes': {
              // Sort by latest note timestamp
              // First try to get from ticker data, then fall back to tickerNotesMap
              let aTimestamp = a.latest_note_updated_at;
              let bTimestamp = b.latest_note_updated_at;

              // If not in ticker data, get from tickerNotesMap
              if (!aTimestamp) {
                const aNotes = tickerNotesMap[a.ticker_id] || [];
                if (aNotes.length > 0 && aNotes[0].updated_at) {
                  aTimestamp = aNotes[0].updated_at;
                }
              }
              if (!bTimestamp) {
                const bNotes = tickerNotesMap[b.ticker_id] || [];
                if (bNotes.length > 0 && bNotes[0].updated_at) {
                  bTimestamp = bNotes[0].updated_at;
                }
              }

              // Tickers with no notes go to the bottom
              if (!aTimestamp && !bTimestamp) {
                aValue = 0;
                bValue = 0;
              } else if (!aTimestamp) {
                return 1; // a goes to the bottom
              } else if (!bTimestamp) {
                return -1; // b goes to the bottom
              } else {
                // Compare timestamps - more recent notes should be higher
                aValue = new Date(aTimestamp).getTime();
                bValue = new Date(bTimestamp).getTime();
              }
              break;
            }
            default:
              aValue = a.symbol;
              bValue = b.symbol;
          }
        }

        // Handle string comparison
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          if (sortDirection === 'asc') {
            return aValue.localeCompare(bValue);
          } else {
            return bValue.localeCompare(aValue);
          }
        }

        // Handle numeric comparison
        if (sortDirection === 'asc') {
          return aValue - bValue;
        } else {
          return bValue - aValue;
        }
      })
      .map(t => t.ticker_id);
    // tickerIdsKey (not tickers) is intentional -- see comment above the
    // memo: this should only recompute when the set of tickers changes or
    // sort criteria change, not on every value-only tickers update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickerIdsKey, sortColumn, sortDirection, multiSortMode, tickerNotesMap, calculatePositionSize, getCurrentTradeInfo, getEffectiveTodayGain, getStrategyCacheKey, getStrategyInfo, getTradeGainAmount]);

  // Final row order + live values: frozen order from sortedTickerIds above,
  // filled in with each ticker's current data so cells (price, gain, etc.)
  // still update live between resorts.
  const sortedTickers = useMemo(() => {
    return sortedTickerIds
      .map(id => tickersById.get(id))
      .filter(Boolean);
  }, [sortedTickerIds, tickersById]);

  const totals = useMemo(() => {
    if (!sortedTickers || sortedTickers.length === 0) return {};

    const totalShares = sortedTickers.reduce((total, ticker) => total + ticker.shares, 0);
    const totalCostBasis = sortedTickers.reduce((total, ticker) => total + (ticker.cost_basis ? ticker.shares * ticker.cost_basis : 0), 0);
    const totalTSize = sortedTickers.reduce((total, ticker) => total + (ticker.tsize !== null && ticker.tsize !== undefined ? ticker.tsize : 0), 0);
    const totalPositionSize = sortedTickers.reduce((total, ticker) => {
      const currentValue = ticker.current_value || (ticker.shares * ticker.cost_basis) || 0;
      const positionSize = accountStandardSize && accountStandardSize > 0 ? currentValue / accountStandardSize : 0;
      return total + positionSize;
    }, 0);
    
    // For current value, use cost basis as fallback when price is unavailable
    const totalCurrentValue = sortedTickers.reduce((total, ticker) => {
      if (ticker.current_price) {
        return total + ticker.current_value;
      } else if (ticker.cost_basis) {
        return total + (ticker.shares * ticker.cost_basis);
      }
      return total;
    }, 0);
    
    // Calculate average pre_close weighted by shares
    const totalPreClose = sortedTickers.reduce((total, ticker) => {
      // Use our general helper function to get a reliable pre_close value
      const preCloseValue = getPreCloseValue(ticker);
      
      // Only add to total if we have a valid value
      if (preCloseValue !== null) {
        return total + (preCloseValue * ticker.shares);
      }
      
      return total;
    }, 0);
    
    const totalSharesForPreClose = sortedTickers.reduce((total, ticker) => {
      // Again, use our helper function to determine if this ticker should be counted
      const preCloseValue = getPreCloseValue(ticker);
      
      if (preCloseValue !== null) {
        return total + ticker.shares;
      }
      return total;
    }, 0);
    
    const avgPreClose = totalSharesForPreClose > 0 ? totalPreClose / totalSharesForPreClose : 0;
    
    const totalTodayGain = sortedTickers.reduce((total, ticker) => total + getEffectiveTodayGain(ticker).amount, 0);
    const totalTotalGain = sortedTickers.reduce((total, ticker) => total + (ticker.total_gain || 0), 0);
    const totalUnrealizedGain = sortedTickers.reduce((total, ticker) => total + (ticker.unrealized_gain || 0), 0);
    const totalTradeGain = sortedTickers.reduce((total, ticker) => {
      const { gainPct } = getCurrentTradeInfo(ticker);
      const amount = getTradeGainAmount(ticker, gainPct);
      return total + (amount || 0);
    }, 0);
    
    // Calculate total gain percentages using cost basis instead of current value
    const totalTodayGainPct = totalCostBasis > 0 ? (totalTodayGain / totalCostBasis) * 100 : 0;
    const totalTotalGainPct = totalCostBasis > 0 ? (totalTotalGain / totalCostBasis) * 100 : 0;
    
    // Calculate total risk values
    const totalRiskAmount = sortedTickers.reduce((total, ticker) => {
      const risk = getRiskGain(ticker);
      return total + (risk.amount || 0);
    }, 0);
    
    // Calculate total risk percentage weighted by current value
    const totalRiskPercentage = sortedTickers.reduce((total, ticker) => {
      const currentValue = ticker.current_value || (ticker.shares * ticker.cost_basis) || 0;
      const riskPercentage = ticker.shield_risk_percentage || 0;
      return total + (currentValue * riskPercentage / 100);
    }, 0);
    
    // Convert to percentage of total current value
    const totalRiskPct = totalCurrentValue > 0 ? (totalRiskPercentage / totalCurrentValue) * 100 : 0;
    
    // Calculate total TRisk(%) as total risk amount divided by total account value
    const totalTRiskPct = accountTotalValue && accountTotalValue > 0 ? (totalRiskAmount / accountTotalValue) * 100 : 0;
    
    return {
      totalShares,
      totalCostBasis,
      totalCurrentValue,
      avgPreClose,
      totalTodayGain,
      totalTodayGainPct,
      totalTotalGain,
      totalTotalGainPct,
      totalUnrealizedGain,
      totalTradeGain,
      totalRiskAmount,
      totalRiskPct,
      totalTRiskPct,
      totalTSize,
      totalPositionSize
    };
  }, [sortedTickers, accountStandardSize, accountTotalValue, getCurrentTradeInfo, getEffectiveTodayGain, getTradeGainAmount, getPreCloseValue]);

  // Helper to render footer cell based on column
  const renderFooterCell = (column) => {
    if (!totals) return null;
    
    // Common cell style for footer cells
    const cellStyle = {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      textAlign: 'right'
    };
    
    // Return null for invisible columns except symbol which is always visible
    if (column !== 'symbol' && !visibleColumns[column]) {
      return null;
    }
    
    switch (column) {
      case 'symbol':
        return <td key={`footer-${column}`} style={{...cellStyle, textAlign: 'left'}}>
          <strong>TOTAL ({sortedTickers.length})</strong>
        </td>;
      case 'strategy':
        return <td key={`footer-${column}`} style={{...cellStyle, textAlign: 'center'}}></td>;
      case 'actions':
        return <td key={`footer-${column}`} style={{...cellStyle, textAlign: 'center'}}></td>;
      case 'shares':
        // No total for shares count per requirements
        return <td key={`footer-${column}`} style={{...cellStyle, textAlign: 'right'}}></td>;
      case 'cost_basis':
        // No total for cost_basis (cost per share) per requirements
        return <td key={`footer-${column}`} style={{...cellStyle, textAlign: 'right'}}></td>;
      case 'total_cost':
        return <td key={`footer-${column}`} style={{...cellStyle, textAlign: 'right'}}><strong>{formatCurrency(totals.totalCostBasis)}</strong></td>;
      case 'pre_close':
        // No total for pre_close per requirements
        return visibleColumns.pre_close ? <td key={`footer-${column}`} style={{...cellStyle, textAlign: 'right'}}></td> : null;
      case 'current_value':
        return <td key={`footer-${column}`} style={{...cellStyle, textAlign: 'right', fontWeight: 'bold', color: '#0d6efd'}}><strong>{formatCurrencyWhole(totals.totalCurrentValue)}</strong></td>;
      case 'high':
        // No total for high column - just show empty cell
        return <td key={`footer-${column}`} style={{...cellStyle, textAlign: 'center'}}></td>;
      case 'today_gain_amount':
        return (
          <td key={`footer-${column}`} style={{...cellStyle, textAlign: 'right'}}>
            <strong className={`text-${getVariant(totals.totalTodayGain)}`}>
              {totals.totalTodayGain >= 0 ? '' : '-'}{formatCurrency(Math.abs(totals.totalTodayGain))}
            </strong>
          </td>
        );
      case 'today_gain_percentage':
        return (
          <td key={`footer-${column}`} style={{...cellStyle, textAlign: 'right'}}>
            <OverlayTrigger
              placement="top"
              delay={{ show: 200, hide: 100 }}
              overlay={
                <Tooltip id="tickers-total-today-gain-percent-tooltip">
                  <span style={{ color: totals.totalTodayGain >= 0 ? '#28d17c' : '#ff3b3b', fontWeight: 700, fontSize: '1.35rem', lineHeight: 1 }}>
                    {totals.totalTodayGain >= 0 ? '' : '-'}{formatCurrency(Math.abs(totals.totalTodayGain))}
                  </span>
                </Tooltip>
              }
              trigger={["hover", "focus", "click"]}
            >
              <strong className={`text-${getVariant(totals.totalTodayGainPct)}`} style={{ cursor: 'pointer' }}>
                {totals.totalTodayGainPct >= 0 ? '' : '-'}{formatPercentage(Math.abs(totals.totalTodayGainPct))}
              </strong>
            </OverlayTrigger>
          </td>
        );
      case 'total_gain':
        return (
          <td key={`footer-${column}`} style={{...cellStyle, textAlign: 'right'}}>
            <strong className={`text-${getVariant(totals.totalTotalGain)}`}>
              {totals.totalTotalGain >= 0 ? '' : '-'}{formatCurrency(Math.abs(totals.totalTotalGain))}
            </strong>
          </td>
        );
      case 'total_gain_pct':
        return (
          <td key={`footer-${column}`} style={{...cellStyle, textAlign: 'right'}}>
            <OverlayTrigger
              placement="top"
              delay={{ show: 200, hide: 100 }}
              overlay={
                <Tooltip id="tickers-total-gain-percent-tooltip">
                  <span style={{ color: totals.totalTotalGain >= 0 ? '#28d17c' : '#ff3b3b', fontWeight: 700, fontSize: '1.35rem', lineHeight: 1 }}>
                    {totals.totalTotalGain >= 0 ? '' : '-'}{formatCurrency(Math.abs(totals.totalTotalGain))}
                  </span>
                </Tooltip>
              }
              trigger={["hover", "focus", "click"]}
            >
              <strong className={`text-${getVariant(totals.totalTotalGainPct)}`} style={{ cursor: 'pointer' }}>
                {totals.totalTotalGainPct >= 0 ? '' : '-'}{formatPercentage(Math.abs(totals.totalTotalGainPct))}
              </strong>
            </OverlayTrigger>
          </td>
        );
      case 'name':
      case 'rating':
      case 'entry':
      case 'source':
      case 'category':
      case 'days_held':
      case 'armor':
      case 'theme':
        return <td key={`footer-${column}`} style={{...cellStyle, textAlign: 'right'}}></td>;
      case 'position_size':
        return (
          <td key={`footer-${column}`} style={{...cellStyle, textAlign: 'right'}}>
            <strong style={{ color: '#0d6efd' }}>{totals.totalPositionSize.toFixed(2)}</strong>
          </td>
        );
      case 'tsize':
        return <td key={`footer-${column}`} style={{...cellStyle, textAlign: 'right'}}><strong>{formatNumber(totals.totalTSize)}</strong></td>;
      case 'risk_gain':
        return <td key={`footer-${column}`} style={{...cellStyle, textAlign: 'right'}}></td>;
      case 'trisk_gain':
        return (
          <td key={`footer-${column}`} style={{...cellStyle, textAlign: 'right'}}>
            <strong 
              className={
                totals.totalTRiskPct > 0
                  ? 'text-success'
                  : (totals.totalTRiskPct >= -2.5 ? 'text-primary' : 'text-danger')
              }
              style={{ 
                fontSize: totals.totalTRiskPct <= -2.5 ? '1.1em' : 'inherit'
              }}
            >
              {totals.totalTRiskPct >= 0 ? '' : '-'}{formatPercentage(Math.abs(totals.totalTRiskPct))}
            </strong>
          </td>
        );
      case 'risk_gain_amount':
        return (
          <td key={`footer-${column}`} style={{...cellStyle, textAlign: 'right'}}>
            <strong className={`text-${getVariant(totals.totalRiskAmount)}`}>
              {totals.totalRiskAmount >= 0 ? '' : '-'}{formatRiskAmount(Math.abs(totals.totalRiskAmount))}
            </strong>
          </td>
        );
      case 'unrealized_gain':
        return (
          <td key={`footer-${column}`} style={{...cellStyle, textAlign: 'right'}}>
            <strong className={`text-${getVariant(totals.totalUnrealizedGain)}`}>
              {totals.totalUnrealizedGain >= 0 ? '' : '-'}{formatCurrencyWhole(Math.abs(totals.totalUnrealizedGain))}
            </strong>
          </td>
        );
      case 'trade_gain':
        return (
          <td key={`footer-${column}`} style={{...cellStyle, textAlign: 'right'}}>
            <strong className={`text-${getVariant(totals.totalTradeGain)}`}>
              {totals.totalTradeGain >= 0 ? '' : '-'}{formatCurrencyWhole(Math.abs(totals.totalTradeGain))}
            </strong>
          </td>
        );
      default:
        return <td key={`footer-${column}`} style={{...cellStyle, textAlign: 'right'}}></td>;
    }
  };

  // New function to toggle expanded rows for multi-account tickers
  const toggleExpandRow = (tickerId) => {
    setExpandedRows(prev => {
      const newState = {
        ...prev,
        [tickerId]: !prev[tickerId]
      };

      // Save to localStorage
      localStorage.setItem('expandedRows', JSON.stringify(newState));

      return newState;
    });
  };

  const collapseAllMultiAccountRows = useCallback(() => {
    setExpandedRows(prev => {
      if (!Array.isArray(tickers) || tickers.length === 0) {
        return prev;
      }

      const updated = { ...prev };
      let changed = false;

      tickers.forEach((ticker) => {
        if (ticker && ticker.in_multiple_accounts && updated[ticker.ticker_id]) {
          delete updated[ticker.ticker_id];
          changed = true;
        }
      });

      if (changed) {
        localStorage.setItem('expandedRows', JSON.stringify(updated));
        return updated;
      }

      return prev;
    });
  }, [tickers]);

  const expandAllMultiAccountRows = useCallback(() => {
    setExpandedRows(prev => {
      if (!Array.isArray(tickers) || tickers.length === 0) {
        return prev;
      }

      const updated = { ...prev };
      let changed = false;

      tickers.forEach((ticker) => {
        if (
          ticker &&
          ticker.in_multiple_accounts &&
          Array.isArray(ticker.accounts) &&
          ticker.accounts.length > 1 &&
          !updated[ticker.ticker_id]
        ) {
          updated[ticker.ticker_id] = true;
          changed = true;
        }
      });

      if (changed) {
        localStorage.setItem('expandedRows', JSON.stringify(updated));
        return updated;
      }

      return prev;
    });
  }, [tickers]);

  useImperativeHandle(ref, () => ({
    collapseAllMultiAccountRows,
    expandAllMultiAccountRows
  }), [collapseAllMultiAccountRows, expandAllMultiAccountRows]);

  // New function to render instance rows for a ticker
  const renderInstanceRows = (ticker) => {
    if (!expandedRows[ticker.ticker_id] || !ticker.accounts || ticker.accounts.length <= 1) {
      return null;
    }

    return ticker.accounts.map((account) => {
      // Use the data from the API for each account
      const shares = account.shares || 0;
      const currentPrice = ticker.current_price || 0;
      const costBasis = account.cost_basis || 0;
      const currentValue = account.current_value || shares * currentPrice;
      const totalCost = costBasis * shares;
      const gain = account.total_gain || (currentValue - totalCost);
      const gainPct = account.total_gain_pct || 0;
      // Add today's gain calculation for sub-ticker accounts
      const todayGainAmount = account.today_gain_amount || 0;
      const todayGainPercentage = account.today_gain_percentage || 0;
      const daysHeldValue = account.days_held || 0;
      // Calculate position size safely, handling case when accountTotalValue is not available
      const positionSize = accountTotalValue && currentValue ? (currentValue / accountTotalValue * 100) : 0;

      return (
        <tr 
          key={`${ticker.ticker_id}-instance-${account.account_id}`} 
          className="instance-row"
        >
          {columnOrder.map(column => {
            if (column !== 'symbol' && !visibleColumns[column]) {
              return null;
            }
            const cellStyle = {
              ...getColumnStyle(column),
              boxSizing: 'border-box',
              padding: '0.25rem', // Match Transaction table cell padding
              verticalAlign: 'middle',
              fontSize: '0.9rem',
              fontWeight: '500',
              borderTop: 'none'
            };

            // For the first column, show the account info instead of symbol
            if (column === 'symbol') {
              return (
                <td key={`instance-${ticker.ticker_id}-${account.account_id}-${column}`} style={{ ...cellStyle, textAlign: 'left', paddingLeft: '2.5rem' }}>
                  <i className="bi bi-arrow-return-right me-1 text-secondary" style={{ fontSize: '0.75rem' }}></i>
                  <Link to={`/account/${account.account_id}`} style={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
                    {account.account_name}
                  </Link>
                  <span className="text-muted ms-2">
                    <small style={{ fontWeight: '500' }}>({account.portfolio_name})</small>
                  </span>
                </td>
              );
            }

            if (column === 'strategy') {
              const strategyInfo = getStrategyInfo(ticker);
              const badgeElement = strategyInfo ? (
                <Badge
                  bg={strategyInfo.variant}
                  className="ticker-strategy-badge"
                  style={{
                    fontSize: '0.65rem',
                    padding: '0.18em 0.5em',
                    fontWeight: '600',
                    letterSpacing: '0.03em',
                    textTransform: 'uppercase',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    color: strategyInfo.textColor || undefined
                  }}
                >
                  {strategyInfo.label}
                </Badge>
              ) : <span className="text-muted">—</span>;

              return (
                <td key={`instance-${ticker.ticker_id}-${account.account_id}-${column}`} style={{ ...cellStyle, textAlign: 'center' }}>
                  {badgeElement}
                </td>
              );
            }

            // Add order/sell buttons to instance rows
            else if (column === 'actions' && visibleColumns[column]) {
              return (
                <td key={`instance-${ticker.ticker_id}-${account.account_id}-${column}`} style={{...cellStyle, textAlign: 'center'}}>
                  <div className="d-flex justify-content-center gap-1">
                    {!hideOrderButton && (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="py-0"
                        style={{ fontSize: '0.75rem' }}
                        onClick={() => onShowOrderDialog && onShowOrderDialog(ticker)}
                      >
                        Order
                      </Button>
                    )}
                    <Button
                      variant="outline-danger"
                      size="sm"
                      className="py-0"
                      style={{ fontSize: '0.75rem' }}
                      onClick={() => onInitiateTransaction && onInitiateTransaction({
                        type: 'SELL',
                        ticker: {
                          ...ticker,
                          account_id: account.account_id,
                          account_name: account.account_name,
                          portfolio_id: account.portfolio_id,
                          portfolio_name: account.portfolio_name,
                          shares: shares
                        },
                        existingShares: shares
                      })}
                    >
                      Sell
                    </Button>
                  </div>
                </td>
              );
            }
            
            // For shares column
            else if (column === 'shares' && visibleColumns[column]) {
              return (
                <td key={`instance-${ticker.ticker_id}-${account.account_id}-${column}`} style={{...cellStyle, textAlign: 'right'}}>
                  {formatNumber(shares)}
                </td>
              );
            }
            
            // For TSize column
            else if (column === 'tsize' && visibleColumns[column]) {
              return (
                <td 
                  key={`instance-${ticker.ticker_id}-${account.account_id}-${column}`} 
                  style={{...cellStyle, textAlign: 'right', cursor: 'pointer'}}
                  onClick={() => handleTSizeClick(ticker)}
                  title="Click to edit target size"
                >
                  {ticker.tsize !== null && ticker.tsize !== undefined ? (
                    <span style={{
                      color: (() => {
                        // Display red if TSize is 0
                        if (ticker.tsize === 0) {
                          return '#dc3545';
                        }
                        const currentValue = ticker.current_value || (ticker.shares * ticker.cost_basis) || 0;
                        const positionSize = accountStandardSize && accountStandardSize > 0 ? currentValue / accountStandardSize : 0;
                        return positionSize >= ticker.tsize ? '#007bff' : 'inherit';
                      })(),
                      fontWeight: (() => {
                        const currentValue = ticker.current_value || (ticker.shares * ticker.cost_basis) || 0;
                        const positionSize = accountStandardSize && accountStandardSize > 0 ? currentValue / accountStandardSize : 0;
                        return positionSize >= ticker.tsize ? 'bold' : 'normal';
                      })()
                    }}>
                      {formatNumber(ticker.tsize)}
                    </span>
                  ) : '-'}
                </td>
              );
            }

            else if (column === 'entry' && visibleColumns[column]) {
              const entryValue = normalizeEntryRatingValue(account.entry_rating);
              const entryStyle = ENTRY_RATING_STYLES[entryValue];
              return (
                <td
                  key={`instance-${ticker.ticker_id}-${account.account_id}-${column}`}
                  style={{ ...cellStyle, textAlign: 'center', cursor: 'pointer' }}
                  onClick={() => openEntryEditor({
                    ...ticker,
                    account_id: account.account_id,
                    entry_rating: account.entry_rating
                  })}
                  title="Set entry rating"
                >
                  {entryStyle ? (
                    <Badge
                      bg={entryStyle.variant}
                      className={entryStyle.textClass}
                      style={{
                        fontSize: '0.72rem',
                        padding: '0.18em 0.5em',
                        fontWeight: '600',
                        letterSpacing: '0.03em',
                        textTransform: 'uppercase',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                      }}
                    >
                      {entryStyle.label}
                    </Badge>
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </td>
              );
            }

            // Skip rendering Next column for instance rows
            else if (column === 'next' && visibleColumns[column]) {
              return (
                <td key={`instance-${ticker.ticker_id}-${account.account_id}-${column}`} style={{...cellStyle, textAlign: 'right'}}>
                  <span className="text-muted">-</span>
                </td>
              );
            }
            
            // For source column
            else if (column === 'source' && visibleColumns[column]) {
              return (
                <td key={`instance-${ticker.ticker_id}-${account.account_id}-${column}`} style={{...cellStyle, textAlign: 'right'}}>
                  <Link to={`/account/${account.account_id}`}>
                    {account.account_name}
                  </Link>
                </td>
              );
            }
            
            // For cost basis column
            else if (column === 'cost_basis' && visibleColumns[column]) {
              return (
                <td key={`instance-${ticker.ticker_id}-${account.account_id}-${column}`} style={cellStyle}>
                  {costBasis ? (
                    <span className={`text-${getVariant(gain)}`} style={{ fontWeight: 'bold' }}>
                      {formatCurrency(costBasis)}
                    </span>
                  ) : '-'}
                </td>
              );
            }

            // For total cost column
            else if (column === 'total_cost' && visibleColumns[column]) {
              return (
                <td key={`instance-${ticker.ticker_id}-${account.account_id}-${column}`} style={cellStyle}>
                  {totalCost ? (
                    <span className={`text-${getVariant(gain)}`} style={{ fontWeight: 'bold' }}>
                      {formatCurrency(totalCost)}
                    </span>
                  ) : '-'}
                </td>
              );
            }
            
            // For current value column
            else if (column === 'current_value' && visibleColumns[column]) {
              return (
                <td key={`instance-${ticker.ticker_id}-${account.account_id}-${column}`} style={{...cellStyle, textAlign: 'right'}}>
                  {formatCurrency(currentValue)}
                </td>
              );
            }
            
            // For high column
            else if (column === 'high' && visibleColumns[column]) {
              return (
                <td key={`instance-${ticker.ticker_id}-${account.account_id}-${column}`} style={{...cellStyle, textAlign: 'center'}}>
                  {renderHighStatusIcon(
                    account.is_new_high_today,
                    `ticker-${ticker.ticker_id}-account-${account.account_id}`
                  )}
                </td>
              );
            }
            
            // For total gain column
            else if (column === 'total_gain' && visibleColumns[column]) {
              return (
                <td key={`instance-${ticker.ticker_id}-${account.account_id}-${column}`} style={cellStyle}>
                  <span className={`text-${getVariant(gain)}`}>
                    {formatCurrency(gain)}
                  </span>
                </td>
              );
            }
            
            // For total gain percentage column
            else if (column === 'total_gain_pct' && visibleColumns[column]) {
              return (
                <td key={`instance-${ticker.ticker_id}-${account.account_id}-${column}`} style={{...cellStyle, textAlign: 'right'}}>
                  <span className={`text-${getVariant(gainPct)}`}>
                    {formatPercentage(gainPct)}
                  </span>
                </td>
              );
            }
            
            // For position size column
            else if (column === 'position_size' && visibleColumns[column]) {
              return (
                <td key={`instance-${ticker.ticker_id}-${account.account_id}-${column}`} style={cellStyle}>
                  <div className="text-end">
                    <span style={{ fontWeight: 'bold', color: '#0d6efd' }}>
                      {positionSize ? positionSize.toFixed(2) : '-'}
                    </span>
                  </div>
                </td>
              );
            }
            
            // For today gain amount column
            else if (column === 'today_gain_amount' && visibleColumns[column]) {
              return (
                <td key={`instance-${ticker.ticker_id}-${account.account_id}-${column}`} style={cellStyle}>
                  <span className={`text-${getVariant(todayGainAmount)}`}>
                    {todayGainAmount >= 0 ? '' : '-'}{formatCurrency(Math.abs(todayGainAmount))}
                  </span>
                </td>
              );
            }
            
            // For today gain percentage column
            else if (column === 'today_gain_percentage' && visibleColumns[column]) {
              return (
                <td key={`instance-${ticker.ticker_id}-${account.account_id}-${column}`} style={{...cellStyle, textAlign: 'right'}}>
                  <span className={`text-${getVariant(todayGainPercentage)}`}>
                    {todayGainPercentage >= 0 ? '' : '-'}{formatPercentage(Math.abs(todayGainPercentage))}
                  </span>
                </td>
              );
            }
            
            // For days held column
            else if (column === 'days_held' && visibleColumns[column]) {
              return (
                <td key={`instance-${ticker.ticker_id}-${account.account_id}-${column}`} style={cellStyle}>
                  <div className="text-end">
                    <span style={{ fontWeight: 'bold', color: '#0dcaf0' }}>
                      {daysHeldValue || '-'}
                    </span>
                  </div>
                </td>
              );
            }
            
            // For armor column
            else if (column === 'armor' && visibleColumns[column]) {
              return (
                <td key={`instance-${ticker.ticker_id}-${account.account_id}-${column}`} style={cellStyle}>
                  <div className="text-center">
                    <ArmorEdit
                      accountId={account.account_id}
                      tickerId={ticker.ticker_id}
                      currentArmor={account.armor !== undefined ? account.armor : ticker.armor}
                      onArmorUpdate={handleArmorUpdate}
                      onShieldUpdate={handleShieldRefresh}
                      tradeId={getCurrentTradeInfo(account).tradeId}
                    />
                  </div>
                </td>
              );
            }

            else if (column === 'theme' && visibleColumns[column]) {
              return (
                <td key={`instance-${ticker.ticker_id}-${account.account_id}-${column}`} style={cellStyle}>
                  <div className="text-center">
                <button
                  type="button"
                  className="btn btn-link p-0"
                  onClick={() => handleThemeToggle({ ...ticker, ticker_id: ticker.ticker_id, account_id: account.account_id, theme_enabled: account.theme_enabled })}
                  aria-label={`Toggle theme ${account.theme_enabled ? 'enabled' : 'disabled'} for ${account.account_name || 'account'}`}
                  title={account.theme_enabled ? 'Theme enabled' : 'Theme disabled'}
                  style={{ textDecoration: 'none' }}
                >
                  {renderThemeIcon(account.theme_enabled, THEME_ICON_SUB_SIZE)}
                </button>
              </div>
            </td>
          );
            }

            else if (column === 'base' && visibleColumns[column]) {
              return (
                <td key={`instance-${ticker.ticker_id}-${account.account_id}-${column}`} style={cellStyle}>
                  <div className="text-center">
                    <button
                      type="button"
                      className="btn btn-link p-0"
                      onClick={() => handleBaseToggle({ ...ticker, ticker_id: ticker.ticker_id, account_id: account.account_id, base_enabled: account.base_enabled })}
                      aria-label={`Toggle base ${account.base_enabled ? 'enabled' : 'disabled'} for ${account.account_name || 'account'}`}
                      title={account.base_enabled ? 'Base enabled' : 'Base disabled'}
                      style={{ textDecoration: 'none' }}
                    >
                      {renderThemeIcon(account.base_enabled, THEME_ICON_SUB_SIZE)}
                    </button>
                  </div>
                </td>
              );
            }

            // For risk column - show placeholder for multi-account instances
            else if (column === 'risk_gain' && visibleColumns[column]) {
              return (
                <td key={`instance-${ticker.ticker_id}-${account.account_id}-${column}`} style={{...cellStyle, textAlign: 'right'}}>
                  <div className="text-end">
                    {(() => {
                      // Only show risk data if this specific account has risk percentage available
                      if (account.shield_risk_percentage === undefined || account.shield_risk_percentage === null) {
                        return <span className="text-muted">-</span>;
                      }
                      
                      const sharesForAccount = account.shares || 0;
                      const costBasisPerShareForAccount = account.cost_basis || 0;
                      const fallbackAmountForAccount = (sharesForAccount * costBasisPerShareForAccount) > 0
                        ? (account.shield_risk_percentage / 100) * (sharesForAccount * costBasisPerShareForAccount)
                        : null;
                      const riskGain = {
                        value: account.shield_risk_percentage,
                        isProfit: account.shield_risk_percentage > 0,
                        amount: account.shield_risk_amount ?? fallbackAmountForAccount
                      };
                      
                      if (riskGain.value === null) return <span className="text-muted">-</span>;
                      
                      return (
                        <span 
                          className={`text-${riskGain.isProfit ? 'success' : 'danger'}`}
                          style={{ fontWeight: 'bold' }}
                        >
                          {formatPercentage(riskGain.value)}
                        </span>
                      );
                    })()}
                  </div>
                </td>
              );
            }
            
            // For risk amount column - show placeholder for multi-account instances
            else if (column === 'risk_gain_amount' && visibleColumns[column]) {
              return (
                <td key={`instance-${ticker.ticker_id}-${account.account_id}-${column}`} style={{...cellStyle, textAlign: 'right'}}>
                  <div className="text-end">
                    {(() => {
                      // Only show risk data if this specific account has risk percentage available
                      if (account.shield_risk_percentage === undefined || account.shield_risk_percentage === null) {
                        return <span className="text-muted">-</span>;
                      }
                      
                      const sharesForAccount = account.shares || 0;
                      const costBasisPerShareForAccount = account.cost_basis || 0;
                      const fallbackAmountForAccount = (sharesForAccount * costBasisPerShareForAccount) > 0
                        ? (account.shield_risk_percentage / 100) * (sharesForAccount * costBasisPerShareForAccount)
                        : null;
                      const riskGain = {
                        value: account.shield_risk_percentage,
                        isProfit: account.shield_risk_percentage > 0,
                        amount: account.shield_risk_amount ?? fallbackAmountForAccount
                      };
                      
                      if (riskGain.amount === null) return <span className="text-muted">-</span>;
                      
                      return (
                        <span 
                          className={`text-${riskGain.isProfit ? 'success' : 'danger'}`}
                          style={{ fontWeight: 'bold' }}
                        >
                          {formatRiskAmount(riskGain.amount)}
                        </span>
                      );
                    })()}
                  </div>
                </td>
              );
            }
            
            // For other columns, render empty cells
            return <td key={`instance-${ticker.ticker_id}-${account.account_id}-${column}`} style={cellStyle}></td>;
          })}
        </tr>
      );
    });
  };

  // Render placeholder rows for loading state
  const renderPlaceholderRows = () => {
    // Generate 5 placeholder rows
    return Array(5).fill(0).map((_, index) => (
      <tr key={`placeholder-${index}`} className="border-bottom">
        {columnOrder.map(column => {
          if (!visibleColumns[column] && column !== 'symbol') {
            return null;
          }
          const cellStyle = {
            ...getColumnStyle(column),
            boxSizing: 'border-box',
            padding: '0.25rem', // Match Transaction table cell padding
            verticalAlign: 'middle'
          };
          // Center align the armor column to match the real data alignment
          if (column === 'armor' || column === 'strategy' || column === 'stop' || column === 'theme') {
            cellStyle.textAlign = 'center';
          }
          return (
            <td key={`placeholder-${index}-${column}`} style={cellStyle}>
              <Placeholder animation="glow">
                <Placeholder xs={column === 'symbol' ? 4 : 6} />
              </Placeholder>
            </td>
          );
        }).filter(Boolean)}
      </tr>
    ));
  };

  // Fetch notes for all tickers on mount or tickers change
  useEffect(() => {
    const baseAccountId = accountId || currentAccountId;
    const cacheKey = baseAccountId ? `tickerNotesCache-${baseAccountId}` : null;

    if (cacheKey) {
      try {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed && typeof parsed === 'object') {
            setTickerNotesMap(parsed);
          }
        }
      } catch (error) {
        console.warn('[TickerTable] Failed to load notes cache from storage', error);
      }
    }

    const fetchNotes = async () => {
      const map = {};
      for (const ticker of tickers) {
        try {
          const res = await getTickerNotes(ticker.ticker_id);
          map[ticker.ticker_id] = res.data;
        } catch (e) {
          map[ticker.ticker_id] = [];
        }
      }
      setTickerNotesMap(map);
      const baseAccountId = accountId || currentAccountId;
      const cacheKey = baseAccountId ? `tickerNotesCache-${baseAccountId}` : null;
      if (cacheKey) {
        try {
          localStorage.setItem(cacheKey, JSON.stringify(map));
        } catch (error) {
          console.warn('[TickerTable] Failed to save notes cache to storage', error);
        }
      }
    };
    if (tickers && tickers.length > 0) fetchNotes();
  }, [tickers, accountId, currentAccountId]);

  const handleNotesClick = (ticker) => {
    // Enrich ticker with computed position_size so NotesModal can display Size/TSz
    const currentValue = ticker.current_value || (ticker.shares * ticker.cost_basis) || 0;
    const posSize = accountStandardSize && accountStandardSize > 0 ? (currentValue / accountStandardSize) : null;
    setSelectedTicker({ ...ticker, position_size: posSize });
    setNotesModalOpen(true);
  };

  const handleNotesModalClose = () => {
    setNotesModalOpen(false);
    setSelectedTicker(null);
  };

  // Context menu handlers
  const handleRowContextMenu = (e, ticker) => {
    e.preventDefault();
    setContextMenu({
      show: true,
      x: e.clientX,
      y: e.clientY,
      ticker: ticker
    });
  };

  const handleContextMenuHide = () => {
    setContextMenu({ show: false, x: 0, y: 0, ticker: null });
  };

  const handleSetManualPrice = () => {
    setManualPriceModal({ show: true, ticker: contextMenu.ticker });
    handleContextMenuHide();
  };

  const handleUseMarketPrice = async () => {
    if (!contextMenu.ticker) return;
    
    try {
      const { clearTickerManualPrice } = await import('../services/api');
      await clearTickerManualPrice(contextMenu.ticker.ticker_id);
      
      // Update the local ticker data
      if (onTickerUpdate) {
        const updatedTickers = tickers.map(ticker => {
          if (ticker.ticker_id === contextMenu.ticker.ticker_id) {
            return {
              ...ticker,
              manual_price: null,
              effective_price: ticker.current_price,
              manual_price_set_at: null
            };
          }
          return ticker;
        });
        onTickerUpdate(updatedTickers);
      }
      
      // Trigger a full data refresh to get updated profit/loss calculations from backend
      if (onRefresh) {
        setTimeout(() => {
          onRefresh();
        }, 500); // Small delay to ensure backend processing is complete
      }
    } catch (error) {
      logger.error('Failed to clear manual price:', error);
    }
    
    handleContextMenuHide();
  };

  const handleManualPriceModalClose = () => {
    setManualPriceModal({ show: false, ticker: null });
  };

  const handleManualPriceSet = (tickerId, newPrice) => {
    // Update the local ticker data with the new manual price
    if (onTickerUpdate) {
      const updatedTickers = tickers.map(ticker => {
        if (ticker.ticker_id === tickerId) {
          return {
            ...ticker,
            manual_price: newPrice,
            effective_price: newPrice || ticker.current_price,
            manual_price_set_at: newPrice ? new Date().toISOString() : null
          };
        }
        return ticker;
      });
      onTickerUpdate(updatedTickers);
    }
    
    // Trigger a full data refresh to get updated profit/loss calculations from backend
    if (onRefresh) {
      setTimeout(() => {
        onRefresh();
      }, 500); // Small delay to ensure backend processing is complete
    }
  };

  const handleTSizeClick = (ticker) => {
    setEditingTSizeTicker(ticker);
    setTSizeEditorOpen(true);
  };

  const handleTSizeEditorClose = () => {
    setTSizeEditorOpen(false);
    setEditingTSizeTicker(null);
  };

  const handleTSizeChange = (newTSize) => {
    if (editingTSizeTicker && onTickerUpdate) {
      const updatedTicker = { ...editingTSizeTicker, tsize: newTSize };
      onTickerUpdate(updatedTicker);
    }
  };

  const openEntryEditor = (ticker) => {
    setEditingEntryTicker(ticker);
    setEntryError('');
    setEntryEditorOpen(true);
  };

  const closeEntryEditor = () => {
    if (entrySaving) {
      return;
    }
    setEntryEditorOpen(false);
    setEditingEntryTicker(null);
    setEntryError('');
  };

  const handleEntryRatingSelect = async (value) => {
    if (!editingEntryTicker || entrySaving) {
      return;
    }
    const targetAccountId = editingEntryTicker.account_id ?? accountId ?? currentAccountId;
    if (!targetAccountId) {
      setEntryError('Account context is missing.');
      return;
    }

    setEntrySaving(true);
    setEntryError('');
    try {
      const response = await updateAccountTickerEntryRating(
        targetAccountId,
        editingEntryTicker.ticker_id,
        value
      );
      const updatedEntry = response?.data?.entry_rating ?? value;
      let aggregatedEntryRating = updatedEntry;
      let updatedAccounts = editingEntryTicker.accounts;
      if (Array.isArray(editingEntryTicker.accounts)) {
        updatedAccounts = editingEntryTicker.accounts.map(account => (
          String(account.account_id) === String(targetAccountId)
            ? { ...account, entry_rating: updatedEntry }
            : account
        ));
        const uniqueEntryRatings = new Set(
          updatedAccounts.map(account => account.entry_rating ?? null)
        );
        aggregatedEntryRating = uniqueEntryRatings.size === 1
          ? Array.from(uniqueEntryRatings)[0]
          : null;
      }
      const updatedTicker = {
        ...editingEntryTicker,
        entry_rating: aggregatedEntryRating,
        accounts: updatedAccounts,
        account_id: targetAccountId
      };
      if (onTickerUpdate) {
        onTickerUpdate(updatedTicker);
      }
      setEntryEditorOpen(false);
      setEditingEntryTicker(null);
    } catch (err) {
      setEntryError(err?.response?.data?.error || 'Failed to update entry rating.');
    } finally {
      setEntrySaving(false);
    }
  };

  // Column order state - initialize with default order
  const [columnOrder, setColumnOrder] = useState([
    'symbol',
    'strategy',
    'stop',
    'actions',
    'notes', // Notes near the front for quick access
    'name',
    'rating',
    'entry',
    'source',
    'category',
    'theme',
    'shares',
    'cost_basis',
    'total_cost',
    'current_price',
    'pre_close',
    'current_value',
    'high', // Add new High column
    'today_gain_amount',
    'today_gain_percentage',
    'total_gain',
    'total_gain_pct',
    'trade_gain_pct',
    'days_held',
    'position_size',
    'tsize',
    'next',
    'armor',
    'risk_gain',
    'trisk_gain',
    'risk_gain_amount'
  ]);
  
  // Use column order from parent if provided
  useEffect(() => {
    if (columnOrderFromParent) {
      const incoming = Array.isArray(columnOrderFromParent) ? columnOrderFromParent : [];
      if (incoming.includes('strategy')) {
        setColumnOrder(incoming);
      } else {
        const withoutStrategy = incoming.filter(col => col !== 'strategy');
        const insertIndex = Math.min(1, withoutStrategy.length);
        withoutStrategy.splice(insertIndex, 0, 'strategy');
        setColumnOrder(withoutStrategy);
      }
    }
  }, [columnOrderFromParent]);

  // Notify parent of column order changes
  const updateColumnOrder = useCallback((newOrder) => {
    setColumnOrder(newOrder);
    if (onColumnOrderChange) {
      onColumnOrderChange(newOrder);
    }
  }, [onColumnOrderChange]);

  // Drag and drop state
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [dragPosition, setDragPosition] = useState(null); // 'left' or 'right'

  // Update multiSortMode when localStorage changes from other components
  useEffect(() => {
    const handleStorageChange = () => {
      const savedMode = localStorage.getItem('multiSortMode');
      if (savedMode === 'alphabetical' || savedMode === 'grouped') {
        setMultiSortMode(savedMode);
      }
    };
    
    // Add event listener for storage changes
    window.addEventListener('storage', handleStorageChange);
    
    // Check on mount too
    handleStorageChange();
    
    // Clean up
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);
  
  // Check if we're in the AggregatedTickers page (hide toggle there since it's in the toolbar)
  useEffect(() => {
    // Always hide multi-sort toggle since it's in the parent component's toolbar
    setHideMultiSortToggle(true);
  }, []);

  // Toggle multi-account sorting mode
  const toggleMultiSortMode = () => {
    const newMode = multiSortMode === 'grouped' ? 'alphabetical' : 'grouped';
    setMultiSortMode(newMode);
    
    // Store in localStorage for persistence
    localStorage.setItem('multiSortMode', newMode);
    
    // Re-apply sorting if we're currently sorting by symbol
    if (sortColumn === 'symbol') {
      // Force a re-render by changing sorting direction and then back
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
      setTimeout(() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc'), 10);
    }
  };

  // Ensure symbol, strategy, stop, and actions remain in their required positions whenever columnOrder changes
  useEffect(() => {
    if (columnOrder.length >= 1) {
      let needsUpdate = false;
      let newOrder = [...columnOrder];

      // Ensure symbol is first
      if (newOrder[0] !== 'symbol') {
        newOrder = ['symbol', ...newOrder.filter(col => col !== 'symbol')];
        needsUpdate = true;
      }

      // Ensure strategy is second
      const filteredAfterSymbol = newOrder.filter(col => col !== 'symbol');
      if (!filteredAfterSymbol.includes('strategy')) {
        newOrder = ['symbol', 'strategy', ...filteredAfterSymbol.filter(col => col !== 'strategy')];
        needsUpdate = true;
      } else if (newOrder[1] !== 'strategy') {
        newOrder = [
          'symbol',
          'strategy',
          ...newOrder.filter(col => col !== 'symbol' && col !== 'strategy')
        ];
        needsUpdate = true;
      }

      // Ensure stop is third (right after strategy)
      const filteredAfterStrategy = newOrder.filter(col => col !== 'symbol' && col !== 'strategy');
      if (!filteredAfterStrategy.includes('stop')) {
        newOrder = ['symbol', 'strategy', 'stop', ...filteredAfterStrategy.filter(col => col !== 'stop')];
        needsUpdate = true;
      } else if (newOrder[2] !== 'stop') {
        newOrder = [
          'symbol',
          'strategy',
          'stop',
          ...newOrder.filter(col => col !== 'symbol' && col !== 'strategy' && col !== 'stop')
        ];
        needsUpdate = true;
      }

      // Ensure actions is fourth
      if (newOrder.indexOf('actions') !== 3) {
        const remaining = newOrder.filter(col => col !== 'symbol' && col !== 'strategy' && col !== 'stop' && col !== 'actions');
        newOrder = ['symbol', 'strategy', 'stop', 'actions', ...remaining];
        needsUpdate = true;
      }

      if (needsUpdate) {
        updateColumnOrder(newOrder);
      }
    }
  }, [columnOrder, updateColumnOrder]);

  return (
    <div>
      <style>{customStyles}</style>
      {/* Column controls - only show if not hidden by parent */}
      {!hideColumnsDropdown && (
        <div className="d-flex justify-content-end mb-3 align-items-center">
          <ColumnVisibilityDropdown
            visibleColumns={visibleColumns}
            columns={Object.keys(visibleColumns).filter(key => key !== 'symbol')}
            getLabel={(column) =>
              // columnDefinitions headers are sometimes JSX (e.g. the shield icon for 'armor'),
              // which can't be searched/lowercased -- fall back to the formatted name for those.
              typeof columnDefinitions[column]?.header === 'string'
                ? columnDefinitions[column].header
                : formatColumnLabel(column)
            }
            onToggleColumn={(column) => toggleColumnVisibility(column)}
            toggleLabel="Columns"
            renderRowExtra={(column) => (
              <span className="d-flex">
                <Button
                  size="sm"
                  variant="outline-secondary"
                  className="p-0 px-1 me-1"
                  onClick={() => moveColumnLeft(column)}
                  disabled={columnOrder.indexOf(column) <= 2} // Don't allow moving before symbol/actions
                >
                  <i className="bi bi-arrow-left"></i>
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  className="p-0 px-1"
                  onClick={() => moveColumnRight(column)}
                  disabled={columnOrder.indexOf(column) >= columnOrder.length - 1}
                >
                  <i className="bi bi-arrow-right"></i>
                </Button>
              </span>
            )}
          >
            <Dropdown.Divider />
            <div className="text-muted small">
              <div>• Drag column headers to reorder</div>
              <div>• Click column headers to sort</div>
            </div>
          </ColumnVisibilityDropdown>
        </div>
      )}
      
      <Table 
        bordered 
        hover 
        responsive 
        size="sm"
        style={{ 
          tableLayout: 'auto', 
          width: '100%',
          overflowX: 'auto'
        }}
        className="ticker-table tickers-table"
      >
        <thead>
          <tr>
            {columnOrder.map(column => renderTableHeader(column))}
          </tr>
        </thead>
        <tbody>
          {tableLoading ? (
            renderPlaceholderRows()
          ) : sortedTickers.length === 0 ? (
            <tr>
              <td colSpan={Object.values(visibleColumns).filter(Boolean).length + 1} className="text-center py-4">
                No tickers found.
              </td>
            </tr>
          ) : (
            sortedTickers.map((ticker) => (
              <React.Fragment key={ticker.ticker_id}>
                <tr
                  className={[
                    ticker.shield_risk_percentage !== undefined &&
                    ticker.total_gain_pct !== undefined &&
                    ticker.total_gain_pct < ticker.shield_risk_percentage
                      ? 'stop-loss-hit-row'
                      : '',
                    ticker.in_multiple_accounts && expandedRows[ticker.ticker_id]
                      ? 'multi-account-expanded'
                      : ''
                  ].filter(Boolean).join(' ') || undefined}
                  onContextMenu={(e) => handleRowContextMenu(e, ticker)}
                >
                  {columnOrder.map(column => renderTableCell(ticker, column))}
                </tr>
                {renderInstanceRows(ticker)}
              </React.Fragment>
            ))
          )}
        </tbody>
        <tfoot>
          {!tableLoading && sortedTickers.length > 0 && (
            <tr className="fw-bold">
              {columnOrder.map(column => renderFooterCell(column))}
            </tr>
          )}
        </tfoot>
      </Table>
      <NotesModal
        show={notesModalOpen}
        onHide={handleNotesModalClose}
        ticker={selectedTicker}
        notes={selectedTicker ? tickerNotesMap[selectedTicker.ticker_id] : []}
        setNotesForTicker={(notes) => setTickerNotesMap((prev) => ({ ...prev, [selectedTicker.ticker_id]: notes }))}
        floating={notesFloating}
        defaultY={notesDefaultY}
        defaultX={notesDefaultX}
      />
      {nextEditorOpen && editingNextTicker && (
        <NextPlanEditor
          show={nextEditorOpen}
          onHide={() => { setNextEditorOpen(false); setEditingNextTicker(null); }}
          ticker={editingNextTicker}
          accountId={accountId || currentAccountId}
          onChange={(updated) => onTickerUpdate && onTickerUpdate(updated)}
        />
      )}
      {tsizeEditorOpen && editingTSizeTicker && (
        <TSizeEditor
          tickerId={editingTSizeTicker.ticker_id}
          symbol={editingTSizeTicker.symbol}
          currentTSize={editingTSizeTicker.tsize}
          onTSizeChange={handleTSizeChange}
          onClose={handleTSizeEditorClose}
          accountId={currentAccountId}
        />
      )}
      {entryEditorOpen && editingEntryTicker && (
        <Modal show={entryEditorOpen} onHide={closeEntryEditor} centered>
          <Modal.Header closeButton={!entrySaving}>
            <Modal.Title>Entry Rating</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {entryError && <div className="text-danger mb-2">{entryError}</div>}
            <div className="d-flex flex-wrap justify-content-center align-items-center gap-3 py-2">
              {Object.entries(ENTRY_RATING_STYLES).map(([value, style]) => {
                const isActive = normalizeEntryRatingValue(editingEntryTicker.entry_rating) === value;
                const variant = isActive ? style.variant : `outline-${style.variant}`;
                return (
                  <Button
                    key={value}
                    variant={variant}
                    className={isActive ? style.textClass : ''}
                    style={{
                      fontSize: '1rem',
                      fontWeight: 600,
                      letterSpacing: '0.02em',
                      padding: '0.5rem 1.1rem',
                      minWidth: '110px'
                    }}
                    onClick={() => handleEntryRatingSelect(value)}
                    disabled={entrySaving}
                  >
                    {style.label}
                  </Button>
                );
              })}
            </div>
          </Modal.Body>
          <Modal.Footer>
            {editingEntryTicker.entry_rating && (
              <Button
                variant="outline-secondary"
                onClick={() => handleEntryRatingSelect(null)}
                disabled={entrySaving}
              >
                Clear
              </Button>
            )}
            <Button variant="secondary" onClick={closeEntryEditor} disabled={entrySaving}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      )}
      
      {/* Context Menu */}
      <ContextMenu
        show={contextMenu.show}
        position={{ x: contextMenu.x, y: contextMenu.y }}
        onHide={handleContextMenuHide}
        items={[
          {
            label: 'Set Price Manually',
            icon: '💰',
            onClick: handleSetManualPrice
          },
          ...(contextMenu.ticker?.manual_price ? [{
            label: 'Use Market Price',
            icon: '📈',
            onClick: handleUseMarketPrice
          }] : [])
        ]}
      />
      
      {/* Manual Price Modal */}
      <ManualPriceModal
        show={manualPriceModal.show}
        onHide={handleManualPriceModalClose}
        ticker={manualPriceModal.ticker}
        onPriceSet={handleManualPriceSet}
      />
    </div>
  );
}));

TickerTable.displayName = 'TickerTable';

export default TickerTable;
